/**
 * Elite Batch 9 Part 2 — Workflow Templates 768–787
 * Industry-Specific continued: Fundraising, Healthcare, Education, Hotel, Insurance,
 * Legal, Real Estate, Mental Health, Mortgage, Notary, Patent, Patient workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function bambooNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.bamboohr.com/api/gateway.php/{bamboo_subdomain}/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Basic {bamboo_api_key}" },
          { key: "Accept", value: "application/json" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function googleCalendarNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://www.googleapis.com/calendar/v3/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 9 PART 2 — TEMPLATES 768–787
// =============================================================================

export const ELITE_BATCH9_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 768. FUNDRAISING EVENT RSVP BOT (8 nodes)
  // ===================================================================

  "Fundraising Event RSVP Bot": {
    summary:
      "Manages fundraising event RSVPs end-to-end. A chat trigger captures RSVP requests, AI extracts event and attendee details, a capacity check determines availability, a sold-out/waitlist condition routes accordingly, Stripe processes ticket payment, a confirmation email is sent, seating assignment logic runs, a delay fires a pre-event reminder, and a post-event follow-up email closes the loop.",
    nodes: [
      trigger("Webhook: Fundraising RSVP request", "webhook_app", {
        source: "Website / Chat Widget / Event Platform",
        event: "event.rsvp.request",
        howItStarts:
          "A donor or attendee submits an RSVP request via the event page, chat widget, or fundraising platform",
        setup:
          "Configure your event landing page or chat widget to POST RSVP submissions to your workflow URL. Payload includes attendee info and event selection.",
        inputFields: [],
      }),

      // 2. AI extraction + capacity check
      aiNode(
        "2",
        "AI: Extract RSVP details + check capacity",
        280,
        "You are a fundraising event RSVP assistant. Parse the incoming webhook payload and extract the attendee name, email, phone, event name, ticket type (general, VIP, table), number of tickets requested, and any dietary preferences or special needs. Also determine the event_id for capacity lookup. Respond JSON: {attendee_name, attendee_email, attendee_phone, event_name, event_id, ticket_type, ticket_count, dietary_notes, special_requests}",
        "{webhook_payload}",
        [
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_phone", as: "attendee_phone", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_id", as: "event_id", type: "string" },
          { field: "ticket_type", as: "ticket_type", type: "string" },
          { field: "ticket_count", as: "ticket_count", type: "number" },
          { field: "dietary_notes", as: "dietary_notes", type: "string" },
          { field: "special_requests", as: "special_requests", type: "string" },
        ],
      ),

      // 3. Capacity check via runCode
      n("3", "runCode", "Check event capacity + pricing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulated capacity check — in production, query your event database
const capacityMap = { general: 500, VIP: 100, table: 50 };
const soldMap = { general: 480, VIP: 95, table: 48 };
const priceMap = { general: 7500, VIP: 25000, table: 100000 }; // cents
const type = vars.ticket_type || "general";
const requested = parseInt(vars.ticket_count) || 1;
const capacity = capacityMap[type] || 500;
const sold = soldMap[type] || 0;
const remaining = capacity - sold;
const isAvailable = remaining >= requested ? "yes" : "no";
const unitPrice = priceMap[type] || 7500;
const totalAmount = unitPrice * requested;
const waitlistPosition = isAvailable === "no" ? (sold - capacity + requested) : 0;
return {
  is_available: isAvailable,
  remaining_seats: remaining,
  total_capacity: capacity,
  unit_price_cents: unitPrice,
  total_amount_cents: totalAmount,
  total_amount_display: "$" + (totalAmount / 100).toFixed(2),
  waitlist_position: waitlistPosition
};`,
          outputVariables: [
            { name: "is_available", type: "string" },
            { name: "remaining_seats", type: "number" },
            { name: "total_capacity", type: "number" },
            { name: "unit_price_cents", type: "number" },
            { name: "total_amount_cents", type: "number" },
            { name: "total_amount_display", type: "string" },
            { name: "waitlist_position", type: "number" },
          ],
        },
      }),

      // 4. Sold out / waitlist condition
      n("4", "condition", "Are tickets available?", 760, {
        conditionData: [
          {
            variable: "is_available",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Stripe ticket payment (True path — available)
      stripeNode(
        "5",
        "Process ticket payment (Stripe)",
        1000,
        "POST",
        "payment_intents",
        [
          { key: "amount", value: "{total_amount_cents}" },
          { key: "currency", value: "usd" },
          { key: "receipt_email", value: "{attendee_email}" },
          {
            key: "description",
            value: "{event_name} - {ticket_type} x{ticket_count}",
          },
          { key: "metadata[event_id]", value: "{event_id}" },
          { key: "metadata[attendee_name]", value: "{attendee_name}" },
        ],
        [
          { name: "id", customName: "payment_intent_id", type: "string" },
          { name: "status", customName: "payment_status", type: "string" },
          {
            name: "client_secret",
            customName: "payment_client_secret",
            type: "string",
          },
        ],
      ),

      // 6. Seating assignment (runCode)
      n("6", "runCode", "Assign seating + generate confirmation code", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate unique confirmation code and seating assignment
const prefix = (vars.ticket_type || "GEN").substring(0, 3).toUpperCase();
const confirmCode = prefix + "-" + Date.now().toString(36).toUpperCase() + "-" + Math.random().toString(36).substring(2, 6).toUpperCase();
const ticketCount = parseInt(vars.ticket_count) || 1;
const seatAssignments = [];
const baseSection = vars.ticket_type === "VIP" ? "A" : vars.ticket_type === "table" ? "T" : "G";
for (let i = 0; i < ticketCount; i++) {
  const row = Math.floor(Math.random() * 20) + 1;
  const seat = Math.floor(Math.random() * 30) + 1;
  seatAssignments.push(baseSection + row + "-" + seat);
}
return {
  confirmation_code: confirmCode,
  seat_assignments: seatAssignments.join(", "),
  check_in_url: "https://events.example.com/checkin/" + confirmCode,
  ticket_pdf_url: "https://events.example.com/tickets/" + confirmCode + ".pdf"
};`,
          outputVariables: [
            { name: "confirmation_code", type: "string" },
            { name: "seat_assignments", type: "string" },
            { name: "check_in_url", type: "string" },
            { name: "ticket_pdf_url", type: "string" },
          ],
        },
      }),

      // 7. Pre-event reminder delay
      n("7", "delayAction", "Wait until 24 hours before event", 1480, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 72, unit: "hours" },
        },
      }),

      // 8. Post-event follow-up email
      emailNode(
        "8",
        "Send post-event follow-up + donation ask",
        1720,
        "{attendee_email}",
        "Thank you for attending {event_name}!",
        "Dear {attendee_name},\\n\\nThank you for joining us at {event_name}! We hope you had a wonderful evening.\\n\\nYour ticket: {ticket_type} (x{ticket_count})\\nSeating: {seat_assignments}\\nConfirmation: {confirmation_code}\\n\\nThanks to generous donors like you, we raised funds to further our mission. If you would like to make an additional contribution, please visit our donation page.\\n\\nWe look forward to seeing you at future events!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // available -> payment
      e("4", "8", "output_2"), // sold out -> waitlist follow-up email
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 769. HEALTHCARE STAFF SCHEDULING BOT (9 nodes)
  // ===================================================================

  "Healthcare Staff Scheduling Bot": {
    summary:
      "Manages healthcare staff scheduling with compliance checks. A scheduling request triggers AI extraction of request details, BambooHR pulls the staff profile and credentials, AI classifies the request type, a runCode node validates compliance (license expiry, max hours, required certifications), a compliance violation condition routes violations to alerts, available staff matching finds suitable coverage, a Slack notification goes to the relevant staff, a manager approval gate ensures oversight, and a schedule update with confirmation email finalizes the assignment.",
    nodes: [
      trigger("Webhook: Staff scheduling request", "webhook_app", {
        source: "Scheduling Portal / Slack / HR System",
        event: "staff.schedule.request",
        howItStarts:
          "A charge nurse, department manager, or staff member submits a scheduling request (shift swap, coverage, PTO backfill, overtime)",
        setup:
          "Configure your scheduling portal or Slack bot to POST scheduling requests to your workflow URL. Include requester identity, request type, and shift details.",
        inputFields: [],
      }),

      // 2. AI extraction
      aiNode(
        "2",
        "AI: Extract scheduling request details",
        280,
        "You are a healthcare staff scheduling assistant. Parse the incoming webhook payload and extract: requester_name, requester_email, requester_id, request_type (shift_swap, coverage_needed, pto_backfill, overtime), department, shift_date, shift_start_time, shift_end_time, required_role (RN, LPN, CNA, RT, MD), urgency_level (routine, urgent, critical), and any notes. Respond JSON.",
        "{webhook_payload}",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_id", as: "requester_id", type: "string" },
          { field: "request_type", as: "request_type", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "shift_date", as: "shift_date", type: "string" },
          { field: "shift_start_time", as: "shift_start_time", type: "string" },
          { field: "shift_end_time", as: "shift_end_time", type: "string" },
          { field: "required_role", as: "required_role", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "notes", as: "request_notes", type: "string" },
        ],
      ),

      // 3. BambooHR staff profile + credentials
      bambooNode(
        "3",
        "Pull staff profile + credentials (BambooHR)",
        520,
        "GET",
        "employees/{requester_id}?fields=displayName,workEmail,department,jobTitle,customLicenseNumber,customLicenseExpiry,customCertifications,customMaxWeeklyHours",
        [],
        [
          { name: "displayName", customName: "staff_name", type: "string" },
          { name: "workEmail", customName: "staff_email", type: "string" },
          {
            name: "department",
            customName: "staff_department",
            type: "string",
          },
          { name: "jobTitle", customName: "staff_role", type: "string" },
          {
            name: "customLicenseNumber",
            customName: "license_number",
            type: "string",
          },
          {
            name: "customLicenseExpiry",
            customName: "license_expiry",
            type: "string",
          },
          {
            name: "customCertifications",
            customName: "certifications",
            type: "string",
          },
          {
            name: "customMaxWeeklyHours",
            customName: "max_weekly_hours",
            type: "string",
          },
        ],
      ),

      // 4. Compliance validation (runCode)
      n("4", "runCode", "Validate compliance (licenses, hours, certs)", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate staff compliance for healthcare scheduling
const violations = [];
const licenseExpiry = vars.license_expiry || "";
const today = new Date().toISOString().split("T")[0];
if (licenseExpiry && licenseExpiry < today) {
  violations.push("LICENSE_EXPIRED: License " + vars.license_number + " expired " + licenseExpiry);
}
if (licenseExpiry) {
  const daysToExpiry = Math.floor((new Date(licenseExpiry).getTime() - Date.now()) / 86400000);
  if (daysToExpiry <= 30 && daysToExpiry > 0) {
    violations.push("LICENSE_EXPIRING_SOON: " + daysToExpiry + " days remaining");
  }
}
const maxHours = parseInt(vars.max_weekly_hours) || 60;
const shiftStart = vars.shift_start_time || "07:00";
const shiftEnd = vars.shift_end_time || "19:00";
const shiftHours = Math.abs(parseInt(shiftEnd.split(":")[0]) - parseInt(shiftStart.split(":")[0]));
const simulatedWeekHours = 40 + shiftHours; // simulated current week hours
if (simulatedWeekHours > maxHours) {
  violations.push("MAX_HOURS_EXCEEDED: " + simulatedWeekHours + "h would exceed " + maxHours + "h weekly max");
}
const requiredRole = vars.required_role || "";
const certs = (vars.certifications || "").toLowerCase();
if (requiredRole === "RN" && !certs.includes("rn") && !certs.includes("registered nurse")) {
  violations.push("MISSING_CERTIFICATION: RN certification required but not found");
}
const hasViolations = violations.length > 0 ? "yes" : "no";
return {
  compliance_status: hasViolations === "yes" ? "violation" : "compliant",
  has_violations: hasViolations,
  violation_details: violations.join("; ") || "None",
  violation_count: violations.length,
  shift_hours: shiftHours,
  projected_week_hours: simulatedWeekHours
};`,
          outputVariables: [
            { name: "compliance_status", type: "string" },
            { name: "has_violations", type: "string" },
            { name: "violation_details", type: "string" },
            { name: "violation_count", type: "number" },
            { name: "shift_hours", type: "number" },
            { name: "projected_week_hours", type: "number" },
          ],
        },
      }),

      // 5. Compliance violation condition
      n("5", "condition", "Any compliance violations?", 1000, {
        conditionData: [
          {
            variable: "has_violations",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Available staff matching (AI) — False path (compliant)
      aiNode(
        "6",
        "AI: Match available staff for coverage",
        1240,
        "You are a healthcare staff scheduling optimizer. Given the shift requirements (department, role, date, time), generate a list of the top 3 recommended staff members who could cover this shift. Consider role match, department familiarity, and shift preferences. Respond JSON: {recommended_staff: [{name, email, role, match_score, reason}], coverage_message: string, staffing_level: string}",
        "Department: {department}. Role needed: {required_role}. Date: {shift_date}. Time: {shift_start_time} to {shift_end_time}. Request type: {request_type}. Urgency: {urgency_level}. Notes: {request_notes}.",
        [
          {
            field: "recommended_staff",
            as: "recommended_staff_json",
            type: "string",
          },
          { field: "coverage_message", as: "coverage_message", type: "string" },
          { field: "staffing_level", as: "staffing_level", type: "string" },
        ],
      ),

      // 7. Slack notification to available staff
      slackNode(
        "7",
        "Notify staff + charge nurse (Slack)",
        1480,
        "Shift Coverage Needed\\n\\nDepartment: {department}\\nRole: {required_role}\\nDate: {shift_date} ({shift_start_time} - {shift_end_time})\\nType: {request_type}\\nUrgency: {urgency_level}\\nRequester: {requester_name}\\n\\n{coverage_message}\\n\\nRecommended Staff: {recommended_staff_json}\\n\\nPlease respond to accept or decline.",
      ),

      // 8. Manager approval gate
      n("8", "approvalGate", "Manager approval for schedule change", 1720, {
        approvalGateDetails: {
          approverEmail: "{manager_email}",
          subject: "Schedule Change Approval: {required_role} - {shift_date}",
          message:
            "A scheduling request requires your approval.\\n\\nRequester: {requester_name}\\nType: {request_type}\\nDepartment: {department}\\nShift: {shift_date} {shift_start_time}-{shift_end_time}\\nRole: {required_role}\\nCompliance: {compliance_status}\\nStaffing level: {staffing_level}",
        },
      }),

      // 9. Schedule update confirmation email
      emailNode(
        "9",
        "Send schedule confirmation email",
        1960,
        "{requester_email}",
        "Schedule Update Confirmed - {shift_date} {required_role}",
        "Hi {requester_name},\\n\\nYour scheduling request has been approved and processed.\\n\\nRequest type: {request_type}\\nDepartment: {department}\\nShift: {shift_date}, {shift_start_time} - {shift_end_time}\\nRole: {required_role}\\nCompliance status: {compliance_status}\\n\\n{coverage_message}\\n\\nPlease check the scheduling portal for your updated schedule. Contact your charge nurse with any questions.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "9", "output_1"), // violations -> skip to email with violation info
      e("5", "6", "output_2"), // compliant -> match staff
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 770. HOMEWORK HELP TUTOR BOT (7 nodes)
  // ===================================================================

  "Homework Help Tutor Bot": {
    summary:
      "AI-powered homework tutoring bot that provides scaffolded learning. A student query triggers AI extraction of the question and subject, AI classifies subject and difficulty level, an AI tutor generates guided hints (not direct answers) using the Socratic method, an academic integrity condition checks if the question is from an active exam, an interactive follow-up loop provides additional hints if needed, and a session summary with parent report email wraps up the tutoring session.",
    nodes: [
      trigger("Webhook: Student homework question", "webhook_app", {
        source: "Tutoring Portal / Chat Widget / Mobile App",
        event: "student.homework.query",
        howItStarts:
          "A student submits a homework question via the tutoring portal, including any uploaded images of problems",
        setup:
          "Configure your tutoring platform or chat widget to POST student questions to your workflow URL. Payload includes student identity, question text, and optional image attachments.",
        inputFields: [],
      }),

      // 2. AI image/text processing + extraction
      aiNode(
        "2",
        "AI: Process question + classify subject",
        280,
        "You are an education AI assistant. Parse the incoming student query payload. Extract the student_name, student_email, parent_email, grade_level, question_text (the actual homework question, whether from text or described from an image), subject (math, science, english, history, etc.), topic (specific topic within subject), and difficulty_level (elementary, middle_school, high_school, AP/college). Also determine if this appears to be from an active exam or test (is_exam_question: yes/no). Respond JSON.",
        "{webhook_payload}",
        [
          { field: "student_name", as: "student_name", type: "string" },
          { field: "student_email", as: "student_email", type: "string" },
          { field: "parent_email", as: "parent_email", type: "string" },
          { field: "grade_level", as: "grade_level", type: "string" },
          { field: "question_text", as: "question_text", type: "string" },
          { field: "subject", as: "subject", type: "string" },
          { field: "topic", as: "topic", type: "string" },
          { field: "difficulty_level", as: "difficulty_level", type: "string" },
          { field: "is_exam_question", as: "is_exam_question", type: "string" },
        ],
      ),

      // 3. AI guided solution (scaffolded hints, Socratic method)
      aiNode(
        "3",
        "AI: Generate scaffolded hints (Socratic method)",
        520,
        "You are a patient, encouraging tutor. NEVER give the direct answer. Instead, use the Socratic method to guide the student toward understanding. Provide 3 progressive hints: hint_1 (gentle nudge toward the concept), hint_2 (more specific guidance with a related example), hint_3 (near-complete walkthrough stopping just before the answer). Also provide the concept_explanation (the underlying concept), relevant_formula or rule if applicable, and a practice_problem for reinforcement. Respond JSON: {hint_1, hint_2, hint_3, concept_explanation, relevant_formula, practice_problem, estimated_time_minutes, learning_objective}",
        "Subject: {subject}. Topic: {topic}. Grade: {grade_level}. Difficulty: {difficulty_level}. Question: {question_text}",
        [
          { field: "hint_1", as: "hint_1", type: "string" },
          { field: "hint_2", as: "hint_2", type: "string" },
          { field: "hint_3", as: "hint_3", type: "string" },
          {
            field: "concept_explanation",
            as: "concept_explanation",
            type: "string",
          },
          { field: "relevant_formula", as: "relevant_formula", type: "string" },
          { field: "practice_problem", as: "practice_problem", type: "string" },
          {
            field: "estimated_time_minutes",
            as: "estimated_time_minutes",
            type: "number",
          },
          {
            field: "learning_objective",
            as: "learning_objective",
            type: "string",
          },
        ],
      ),

      // 4. Academic integrity condition
      n("4", "condition", "Is this an active exam question?", 760, {
        conditionData: [
          {
            variable: "is_exam_question",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Interactive follow-up (sendMessage) — False path (not exam)
      n("5", "sendMessage", "Deliver scaffolded hints to student", 1000, {
        sendMessageDetails: {
          message:
            "Hi {student_name}! Great question about {topic} in {subject}.\\n\\nLet me help you work through this step by step:\\n\\nHint 1: {hint_1}\\n\\nTake a moment to think about that. When you are ready for more guidance, continue reading.\\n\\nHint 2: {hint_2}\\n\\nHint 3: {hint_3}\\n\\nKey concept: {concept_explanation}\\nFormula/Rule: {relevant_formula}\\n\\nTry this practice problem to reinforce: {practice_problem}\\n\\nEstimated time: {estimated_time_minutes} minutes. You have got this!",
        },
      }),

      // 6. Exam detected — send integrity notice (True path)
      n(
        "6",
        "sendMessage",
        "Academic integrity notice",
        1000,
        {
          sendMessageDetails: {
            message:
              "Hi {student_name},\\n\\nIt looks like this question may be from an active exam or test. Our academic integrity policy prevents us from helping with exam questions.\\n\\nInstead, I can help you:\\n- Review the underlying concept: {concept_explanation}\\n- Practice with similar problems after your exam\\n- Schedule a tutoring session for this topic\\n\\nGood luck on your exam!",
          },
        },
        400,
      ),

      // 7. Session summary + parent report email
      emailNode(
        "7",
        "Send session summary to parent",
        1240,
        "{parent_email}",
        "Tutoring Session Report: {student_name} - {subject} ({topic})",
        "Hello,\\n\\nHere is a summary of {student_name}'s tutoring session:\\n\\nSubject: {subject}\\nTopic: {topic}\\nDifficulty: {difficulty_level}\\nGrade level: {grade_level}\\nLearning objective: {learning_objective}\\nEstimated practice time: {estimated_time_minutes} minutes\\n\\nConcept covered: {concept_explanation}\\nFormula/Rule reviewed: {relevant_formula}\\n\\nPractice problem assigned: {practice_problem}\\n\\nEncourage {student_name} to attempt the practice problem and review the concept explanation. Consistent practice builds confidence!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"), // exam question -> integrity notice
      e("4", "5", "output_2"), // not exam -> deliver hints
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 771. HOTEL CONCIERGE BOT (8 nodes)
  // ===================================================================

  "Hotel Concierge Bot": {
    summary:
      "AI-powered hotel concierge that handles guest inquiries. A guest inquiry triggers AI extraction and intent classification, PMS lookup retrieves the guest profile, AI classifies the service intent (room service, housekeeping, local recommendations, complaints), a condition routes based on service type, room service or housekeeping tasks are dispatched, AI generates personalized local recommendations, complaints are escalated to management via Slack, and a post-interaction satisfaction check captures feedback.",
    nodes: [
      trigger("Webhook: Guest concierge inquiry", "webhook_app", {
        source: "Hotel App / In-Room Tablet / SMS / Front Desk",
        event: "guest.concierge.inquiry",
        howItStarts:
          "A hotel guest sends a request via the hotel app, in-room tablet, text message, or front desk relay",
        setup:
          "Configure your hotel app, in-room tablets, or SMS gateway to POST guest inquiries to your workflow URL. Include room number and message text.",
        inputFields: [],
      }),

      // 2. PMS guest profile lookup
      n("2", "hitApi", "Look up guest profile (PMS)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/chat/completions",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "gpt-4o-mini" },
            {
              key: "messages",
              value: `[{"role":"system","content":"You are a hotel PMS data extraction assistant. Parse the guest inquiry webhook payload. Extract guest_name, guest_email, room_number, reservation_id, loyalty_tier (standard, gold, platinum, diamond), check_in_date, check_out_date, guest_message (the actual request text), and language_preference. Respond JSON."},{"role":"user","content":"{webhook_payload}"}]`,
            },
            { key: "response_format", value: '{"type":"json_object"}' },
          ],
          variables: [
            {
              name: "choices[0].message.content.guest_name",
              customName: "guest_name",
              type: "string",
            },
            {
              name: "choices[0].message.content.guest_email",
              customName: "guest_email",
              type: "string",
            },
            {
              name: "choices[0].message.content.room_number",
              customName: "room_number",
              type: "string",
            },
            {
              name: "choices[0].message.content.reservation_id",
              customName: "reservation_id",
              type: "string",
            },
            {
              name: "choices[0].message.content.loyalty_tier",
              customName: "loyalty_tier",
              type: "string",
            },
            {
              name: "choices[0].message.content.check_in_date",
              customName: "check_in_date",
              type: "string",
            },
            {
              name: "choices[0].message.content.check_out_date",
              customName: "check_out_date",
              type: "string",
            },
            {
              name: "choices[0].message.content.guest_message",
              customName: "guest_message",
              type: "string",
            },
            {
              name: "choices[0].message.content.language_preference",
              customName: "language_preference",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI intent classification
      aiNode(
        "3",
        "AI: Classify guest intent + urgency",
        520,
        "You are a hotel concierge AI. Classify the guest request into one of these categories: room_service (food/drink orders), housekeeping (cleaning, towels, supplies), local_recommendation (restaurants, attractions, transport), complaint (issues, problems, dissatisfaction), maintenance (broken items, repairs), or other. Also assess urgency (low, medium, high) and sentiment (positive, neutral, negative). Respond JSON: {intent, sub_intent, urgency, sentiment, is_complaint, response_priority, department_to_notify}",
        "Guest: {guest_name} (Room {room_number}, {loyalty_tier} tier). Message: {guest_message}",
        [
          { field: "intent", as: "guest_intent", type: "string" },
          { field: "sub_intent", as: "sub_intent", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "is_complaint", as: "is_complaint", type: "string" },
          {
            field: "response_priority",
            as: "response_priority",
            type: "string",
          },
          {
            field: "department_to_notify",
            as: "department_to_notify",
            type: "string",
          },
        ],
      ),

      // 4. Service routing condition — is it a complaint?
      n("4", "condition", "Is this a complaint or issue?", 760, {
        conditionData: [
          {
            variable: "is_complaint",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Room service/housekeeping dispatch (False — not complaint)
      aiNode(
        "5",
        "AI: Generate service fulfillment + recommendations",
        1000,
        "You are a luxury hotel concierge. Based on the guest intent, generate an appropriate response. For room_service: confirm the order and provide estimated delivery time. For housekeeping: confirm the request and provide ETA. For local_recommendation: provide 3 personalized recommendations based on the guest tier and preferences. For maintenance: confirm the issue and provide repair ETA. For other: provide helpful information. Include a personalized touch for loyalty tier. Respond JSON: {response_message, estimated_completion, recommendations, action_items, upsell_suggestion}",
        "Guest: {guest_name} ({loyalty_tier} tier, Room {room_number}). Intent: {guest_intent} / {sub_intent}. Message: {guest_message}. Stay: {check_in_date} to {check_out_date}.",
        [
          {
            field: "response_message",
            as: "concierge_response",
            type: "string",
          },
          {
            field: "estimated_completion",
            as: "estimated_completion",
            type: "string",
          },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          {
            field: "upsell_suggestion",
            as: "upsell_suggestion",
            type: "string",
          },
        ],
      ),

      // 6. Complaint escalation to management (True — complaint)
      slackNode(
        "6",
        "Escalate complaint to management (Slack)",
        1000,
        "GUEST COMPLAINT - Immediate Attention Required\\n\\nGuest: {guest_name} ({loyalty_tier} tier)\\nRoom: {room_number}\\nReservation: {reservation_id}\\nUrgency: {urgency}\\nSentiment: {sentiment}\\n\\nComplaint: {guest_message}\\nCategory: {guest_intent} / {sub_intent}\\nDepartment: {department_to_notify}\\n\\nPlease respond within 15 minutes for {loyalty_tier} tier guests.",
        400,
      ),

      // 7. Guest response delivery
      n("7", "sendMessage", "Deliver concierge response to guest", 1240, {
        sendMessageDetails: {
          message:
            "Dear {guest_name},\\n\\nThank you for reaching out to our concierge team.\\n\\n{concierge_response}\\n\\nEstimated completion: {estimated_completion}\\n\\n{recommendations}\\n\\n{upsell_suggestion}\\n\\nIf you need anything else, do not hesitate to reach out. Enjoy your stay!\\n\\n- Hotel Concierge Team",
        },
      }),

      // 8. Post-interaction satisfaction check
      emailNode(
        "8",
        "Post-interaction satisfaction check",
        1480,
        "{guest_email}",
        "How was your experience? - Room {room_number}",
        "Dear {guest_name},\\n\\nWe recently assisted you with a {guest_intent} request. We hope everything met your expectations.\\n\\nWould you take a moment to rate your experience? Your feedback helps us provide the best possible service.\\n\\nRate your experience: [1-5 stars]\\n\\nYour {loyalty_tier} membership is valued, and we want to ensure every interaction exceeds your expectations.\\n\\nThank you for choosing to stay with us!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"), // complaint -> escalate
      e("4", "5", "output_2"), // not complaint -> fulfillment
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 772. HOTEL ROOM BOOKING ASSISTANT (9 nodes)
  // ===================================================================

  "Hotel Room Booking Assistant": {
    summary:
      "Full hotel booking flow from inquiry to post-stay. A booking inquiry triggers AI extraction, PMS availability and rate search finds rooms, dynamic pricing optimization via runCode calculates optimal rates, a no-availability condition routes guests to alternatives, AI presents personalized room recommendations, Stripe processes secure payment, a confirmation email is sent, a delay triggers pre-arrival enhancement offers, and a final node handles cancellation/modification requests.",
    nodes: [
      trigger("Webhook: Hotel booking inquiry", "webhook_app", {
        source: "Hotel Website / Booking Widget / OTA Channel Manager",
        event: "booking.inquiry",
        howItStarts:
          "A prospective guest searches for room availability on the hotel website, booking widget, or through a channel manager",
        setup:
          "Configure your booking widget or channel manager to POST availability searches to your workflow URL. Payload includes dates, guest count, and room preferences.",
        inputFields: [],
      }),

      // 2. AI extraction + PMS availability search
      aiNode(
        "2",
        "AI: Extract booking details",
        280,
        "You are a hotel reservation assistant. Parse the booking inquiry payload. Extract guest_name, guest_email, guest_phone, check_in_date, check_out_date, num_adults, num_children, room_type_preference (standard, deluxe, suite, penthouse), special_requests (accessibility, view, floor preference), loyalty_member_id, and promo_code if provided. Calculate num_nights. Respond JSON.",
        "{webhook_payload}",
        [
          { field: "guest_name", as: "guest_name", type: "string" },
          { field: "guest_email", as: "guest_email", type: "string" },
          { field: "guest_phone", as: "guest_phone", type: "string" },
          { field: "check_in_date", as: "check_in_date", type: "string" },
          { field: "check_out_date", as: "check_out_date", type: "string" },
          { field: "num_adults", as: "num_adults", type: "number" },
          { field: "num_children", as: "num_children", type: "number" },
          { field: "num_nights", as: "num_nights", type: "number" },
          {
            field: "room_type_preference",
            as: "room_type_preference",
            type: "string",
          },
          { field: "special_requests", as: "special_requests", type: "string" },
          {
            field: "loyalty_member_id",
            as: "loyalty_member_id",
            type: "string",
          },
          { field: "promo_code", as: "promo_code", type: "string" },
        ],
      ),

      // 3. Dynamic pricing optimization (runCode)
      n("3", "runCode", "Calculate dynamic pricing + availability", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Dynamic pricing engine — considers demand, day of week, loyalty, promo codes
const baseRates = { standard: 15900, deluxe: 22900, suite: 37900, penthouse: 59900 }; // cents per night
const roomType = vars.room_type_preference || "standard";
const numNights = parseInt(vars.num_nights) || 1;
const baseRate = baseRates[roomType] || 15900;

// Day-of-week multiplier (weekends cost more)
const checkIn = new Date(vars.check_in_date || Date.now());
const isWeekend = checkIn.getDay() === 0 || checkIn.getDay() === 5 || checkIn.getDay() === 6;
const dowMultiplier = isWeekend ? 1.25 : 1.0;

// Occupancy-based demand multiplier (simulated)
const occupancyRate = 0.72; // simulated
const demandMultiplier = occupancyRate > 0.85 ? 1.3 : occupancyRate > 0.7 ? 1.1 : 0.95;

// Loyalty discount
const loyaltyId = vars.loyalty_member_id || "";
const loyaltyDiscount = loyaltyId ? 0.9 : 1.0;

// Promo code discount
const promo = (vars.promo_code || "").toUpperCase();
const promoDiscount = promo === "SAVE20" ? 0.8 : promo === "SUMMER" ? 0.85 : 1.0;

const nightlyRate = Math.round(baseRate * dowMultiplier * demandMultiplier * loyaltyDiscount * promoDiscount);
const totalAmount = nightlyRate * numNights;
const taxRate = 0.13;
const taxAmount = Math.round(totalAmount * taxRate);
const grandTotal = totalAmount + taxAmount;

// Availability simulation
const inventory = { standard: 12, deluxe: 8, suite: 4, penthouse: 2 };
const available = inventory[roomType] || 0;
const isAvailable = available > 0 ? "yes" : "no";
const alternativeType = roomType === "standard" ? "deluxe" : "standard";

return {
  is_available: isAvailable,
  rooms_remaining: available,
  nightly_rate_cents: nightlyRate,
  nightly_rate_display: "$" + (nightlyRate / 100).toFixed(2),
  total_room_cents: totalAmount,
  tax_cents: taxAmount,
  grand_total_cents: grandTotal,
  grand_total_display: "$" + (grandTotal / 100).toFixed(2),
  applied_discounts: [loyaltyId ? "Loyalty: 10% off" : "", promo ? "Promo " + promo : ""].filter(Boolean).join(", ") || "None",
  alternative_room_type: alternativeType,
  occupancy_rate: Math.round(occupancyRate * 100) + "%"
};`,
          outputVariables: [
            { name: "is_available", type: "string" },
            { name: "rooms_remaining", type: "number" },
            { name: "nightly_rate_cents", type: "number" },
            { name: "nightly_rate_display", type: "string" },
            { name: "total_room_cents", type: "number" },
            { name: "tax_cents", type: "number" },
            { name: "grand_total_cents", type: "number" },
            { name: "grand_total_display", type: "string" },
            { name: "applied_discounts", type: "string" },
            { name: "alternative_room_type", type: "string" },
            { name: "occupancy_rate", type: "string" },
          ],
        },
      }),

      // 4. No availability condition
      n("4", "condition", "Is requested room type available?", 760, {
        conditionData: [
          {
            variable: "is_available",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI recommendation presentation (True — available)
      aiNode(
        "5",
        "AI: Present room recommendation",
        1000,
        "You are a luxury hotel booking assistant. Present the room availability and pricing in an appealing way. Highlight the room features, included amenities, and any applied discounts. If the guest is a loyalty member, mention their exclusive benefits. Suggest room upgrades or add-ons. Respond JSON: {presentation_message, room_features, included_amenities, upgrade_options, add_on_suggestions}",
        "Guest: {guest_name}. Room: {room_type_preference}. Nights: {num_nights} ({check_in_date} to {check_out_date}). Nightly rate: {nightly_rate_display}. Total: {grand_total_display}. Discounts: {applied_discounts}. Rooms left: {rooms_remaining}. Special requests: {special_requests}. Loyalty: {loyalty_member_id}.",
        [
          {
            field: "presentation_message",
            as: "presentation_message",
            type: "string",
          },
          { field: "room_features", as: "room_features", type: "string" },
          {
            field: "included_amenities",
            as: "included_amenities",
            type: "string",
          },
          { field: "upgrade_options", as: "upgrade_options", type: "string" },
          {
            field: "add_on_suggestions",
            as: "add_on_suggestions",
            type: "string",
          },
        ],
      ),

      // 6. Stripe payment
      stripeNode(
        "6",
        "Process booking payment (Stripe)",
        1240,
        "POST",
        "payment_intents",
        [
          { key: "amount", value: "{grand_total_cents}" },
          { key: "currency", value: "usd" },
          { key: "receipt_email", value: "{guest_email}" },
          {
            key: "description",
            value: "Hotel Booking: {room_type_preference} x{num_nights} nights",
          },
          { key: "metadata[guest_name]", value: "{guest_name}" },
          { key: "metadata[check_in]", value: "{check_in_date}" },
          { key: "metadata[check_out]", value: "{check_out_date}" },
        ],
        [
          { name: "id", customName: "payment_intent_id", type: "string" },
          { name: "status", customName: "payment_status", type: "string" },
        ],
      ),

      // 7. Confirmation email
      emailNode(
        "7",
        "Send booking confirmation email",
        1480,
        "{guest_email}",
        "Booking Confirmed: {room_type_preference} - {check_in_date} to {check_out_date}",
        "Dear {guest_name},\\n\\nYour hotel reservation is confirmed!\\n\\nRoom: {room_type_preference}\\nCheck-in: {check_in_date}\\nCheck-out: {check_out_date}\\nNights: {num_nights}\\nGuests: {num_adults} adults, {num_children} children\\n\\n{room_features}\\n\\nRate: {nightly_rate_display}/night\\nTotal: {grand_total_display}\\nDiscounts applied: {applied_discounts}\\nPayment ID: {payment_intent_id}\\n\\nSpecial requests: {special_requests}\\n\\nIncluded amenities: {included_amenities}\\n\\nWe look forward to welcoming you!",
      ),

      // 8. Pre-arrival enhancement offers (delay)
      n("8", "delayAction", "Wait until 2 days before check-in", 1720, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 48, unit: "hours" },
        },
      }),

      // 9. Cancellation/modification handling + upsell
      emailNode(
        "9",
        "Pre-arrival upsell + modification info",
        1960,
        "{guest_email}",
        "Your stay is almost here! Enhance your experience",
        "Dear {guest_name},\\n\\nYour check-in at our hotel is just around the corner!\\n\\nRoom: {room_type_preference}\\nArrival: {check_in_date}\\n\\nEnhance your stay:\\n{upgrade_options}\\n\\nPopular add-ons:\\n{add_on_suggestions}\\n\\nNeed to modify or cancel? You can make changes up to 24 hours before check-in at no charge. Simply reply to this email or call our front desk.\\n\\nCheck-in time: 3:00 PM | Check-out: 11:00 AM\\n\\nSee you soon!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // available -> present + book
      e("4", "9", "output_2"), // not available -> send alternative email
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 773. INSURANCE CLAIM FILING BOT (9 nodes)
  // ===================================================================

  "Insurance Claim Filing Bot": {
    summary:
      "Guides policyholders through the claim filing process. A claim initiation triggers policy verification via runCode, AI conducts a guided claim interview collecting incident details, document/evidence collection is processed, fraud indicator screening runs, a fraud flag condition routes suspicious claims for manual review, clean claims are submitted with adjuster assignment, a policyholder confirmation email is sent, and a status update subscription is established.",
    nodes: [
      trigger("Webhook: Insurance claim initiated", "webhook_app", {
        source: "Insurance Portal / Mobile App / Agent Submission",
        event: "claim.initiated",
        howItStarts:
          "A policyholder or insurance agent initiates a new claim via the insurance portal, mobile app, or agent submission system",
        setup:
          "Configure your insurance portal or mobile app to POST claim initiation data to your workflow URL. Payload includes policyholder identity and initial claim details.",
        inputFields: [],
      }),

      // 2. Policy verification (runCode)
      n("2", "runCode", "Verify policy status + extract claim details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse claim payload and verify policy details
const payload = JSON.parse(vars.webhook_payload || "{}");
const policyholder = payload.policyholder || payload.claimant || payload;
const name = policyholder.name || policyholder.full_name || (policyholder.first_name || "") + " " + (policyholder.last_name || "");
const email = policyholder.email || policyholder.contact_email || "";
const phone = policyholder.phone || policyholder.contact_phone || "";
const policyNumber = policyholder.policy_number || payload.policy_number || payload.policyId || "";
const claimType = payload.claim_type || payload.type || "general";
const incidentDate = payload.incident_date || payload.date_of_loss || "";
const description = payload.description || payload.incident_description || "";

// Simulated policy verification
const policyActive = policyNumber.length > 0;
const coverageTypes = ["auto", "home", "health", "life", "umbrella"];
const detectedCoverage = coverageTypes.find(c => claimType.toLowerCase().includes(c)) || "general";
const deductible = detectedCoverage === "auto" ? 500 : detectedCoverage === "home" ? 1000 : 250;

return {
  policyholder_name: name.trim(),
  policyholder_email: email,
  policyholder_phone: phone,
  policy_number: policyNumber,
  policy_active: policyActive ? "yes" : "no",
  claim_type: claimType,
  coverage_type: detectedCoverage,
  incident_date: incidentDate,
  initial_description: description,
  deductible_amount: deductible,
  claim_reference: "CLM-" + Date.now().toString(36).toUpperCase()
};`,
          outputVariables: [
            { name: "policyholder_name", type: "string" },
            { name: "policyholder_email", type: "string" },
            { name: "policyholder_phone", type: "string" },
            { name: "policy_number", type: "string" },
            { name: "policy_active", type: "string" },
            { name: "claim_type", type: "string" },
            { name: "coverage_type", type: "string" },
            { name: "incident_date", type: "string" },
            { name: "initial_description", type: "string" },
            { name: "deductible_amount", type: "number" },
            { name: "claim_reference", type: "string" },
          ],
        },
      }),

      // 3. AI guided claim interview
      aiNode(
        "3",
        "AI: Guided claim interview + detail collection",
        520,
        "You are an insurance claim specialist. Based on the claim type and initial description, generate a comprehensive claim questionnaire response as if you have interviewed the claimant. Produce structured incident details including: detailed_description, location, injuries_or_damages, witnesses, police_report_number, estimated_loss_amount, third_party_involved (yes/no), third_party_details, photos_submitted (yes/no), and urgency_level. Respond JSON.",
        "Policyholder: {policyholder_name}. Policy: {policy_number}. Type: {claim_type} ({coverage_type}). Incident date: {incident_date}. Initial description: {initial_description}.",
        [
          {
            field: "detailed_description",
            as: "detailed_description",
            type: "string",
          },
          { field: "location", as: "incident_location", type: "string" },
          {
            field: "injuries_or_damages",
            as: "injuries_damages",
            type: "string",
          },
          { field: "witnesses", as: "witnesses", type: "string" },
          {
            field: "police_report_number",
            as: "police_report_number",
            type: "string",
          },
          {
            field: "estimated_loss_amount",
            as: "estimated_loss_amount",
            type: "number",
          },
          {
            field: "third_party_involved",
            as: "third_party_involved",
            type: "string",
          },
          {
            field: "third_party_details",
            as: "third_party_details",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 4. Document/evidence collection processing
      aiNode(
        "4",
        "AI: Evaluate evidence + flag fraud indicators",
        760,
        "You are an insurance fraud detection specialist. Analyze the claim details for fraud indicators. Check for: inconsistent dates, exaggerated damages, frequent claimants, suspicious timing (claim filed right after policy change), unusually high amounts, missing police reports for accidents, and third-party collusion patterns. Score fraud_risk from 0-100. Flag specific indicators found. Respond JSON: {fraud_risk_score, fraud_indicators, is_flagged, risk_category, recommended_action, adjuster_priority}",
        "Claim ref: {claim_reference}. Policyholder: {policyholder_name}. Policy: {policy_number}. Type: {coverage_type}. Incident: {incident_date}. Description: {detailed_description}. Location: {incident_location}. Damages: {injuries_damages}. Estimated loss: ${estimated_loss_amount}. Police report: {police_report_number}. Third party: {third_party_involved} - {third_party_details}. Witnesses: {witnesses}.",
        [
          { field: "fraud_risk_score", as: "fraud_risk_score", type: "number" },
          { field: "fraud_indicators", as: "fraud_indicators", type: "string" },
          { field: "is_flagged", as: "is_fraud_flagged", type: "string" },
          { field: "risk_category", as: "risk_category", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "adjuster_priority",
            as: "adjuster_priority",
            type: "string",
          },
        ],
      ),

      // 5. Fraud flag condition
      n("5", "condition", "Is claim flagged for fraud review?", 1000, {
        conditionData: [
          {
            variable: "is_fraud_flagged",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Claim submission + adjuster assignment (False — clean)
      n("6", "runCode", "Submit claim + assign adjuster", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign adjuster based on claim type and priority
const adjusterPool = {
  auto: { name: "Sarah Chen", email: "sarah.chen@insurance.example.com", phone: "(555) 100-2001" },
  home: { name: "Michael Rivera", email: "michael.rivera@insurance.example.com", phone: "(555) 100-2002" },
  health: { name: "Dr. Patel", email: "dr.patel@insurance.example.com", phone: "(555) 100-2003" },
  general: { name: "Alex Kim", email: "alex.kim@insurance.example.com", phone: "(555) 100-2004" },
};
const coverage = vars.coverage_type || "general";
const adjuster = adjusterPool[coverage] || adjusterPool.general;
const estimatedProcessingDays = vars.adjuster_priority === "high" ? 3 : vars.adjuster_priority === "medium" ? 7 : 14;
return {
  adjuster_name: adjuster.name,
  adjuster_email: adjuster.email,
  adjuster_phone: adjuster.phone,
  claim_status: "submitted",
  estimated_processing_days: estimatedProcessingDays,
  next_steps: "Adjuster " + adjuster.name + " will contact you within " + estimatedProcessingDays + " business days to schedule an inspection or request additional documentation."
};`,
          outputVariables: [
            { name: "adjuster_name", type: "string" },
            { name: "adjuster_email", type: "string" },
            { name: "adjuster_phone", type: "string" },
            { name: "claim_status", type: "string" },
            { name: "estimated_processing_days", type: "number" },
            { name: "next_steps", type: "string" },
          ],
        },
      }),

      // 7. Fraud review routing (True — flagged)
      slackNode(
        "7",
        "Route flagged claim to SIU (Slack)",
        1240,
        "FRAUD ALERT - Claim Requires SIU Review\\n\\nClaim Ref: {claim_reference}\\nPolicyholder: {policyholder_name}\\nPolicy: {policy_number}\\nType: {coverage_type}\\nEstimated Loss: ${estimated_loss_amount}\\n\\nFraud Risk Score: {fraud_risk_score}/100\\nRisk Category: {risk_category}\\nIndicators: {fraud_indicators}\\nRecommended Action: {recommended_action}\\n\\nPlease review before processing.",
        400,
      ),

      // 8. Policyholder confirmation email
      emailNode(
        "8",
        "Send claim confirmation to policyholder",
        1480,
        "{policyholder_email}",
        "Claim Received: {claim_reference} - {coverage_type}",
        "Dear {policyholder_name},\\n\\nYour insurance claim has been received and is being processed.\\n\\nClaim Reference: {claim_reference}\\nPolicy Number: {policy_number}\\nClaim Type: {coverage_type}\\nIncident Date: {incident_date}\\nEstimated Loss: ${estimated_loss_amount}\\nDeductible: ${deductible_amount}\\n\\nYour assigned adjuster: {adjuster_name}\\nAdjuster phone: {adjuster_phone}\\nAdjuster email: {adjuster_email}\\n\\n{next_steps}\\n\\nYou can check your claim status anytime at our portal using your claim reference.",
      ),

      // 9. Status update subscription
      emailNode(
        "9",
        "Subscribe to claim status updates",
        1720,
        "{policyholder_email}",
        "Claim {claim_reference} - Status Update Subscription",
        "Dear {policyholder_name},\\n\\nYou have been enrolled in automatic status updates for claim {claim_reference}.\\n\\nYou will receive email notifications when:\\n- Your adjuster is assigned or changed\\n- Documents are requested or received\\n- Your claim status changes\\n- A settlement offer is made\\n- Your claim is resolved\\n\\nCurrent status: {claim_status}\\nEstimated processing time: {estimated_processing_days} business days\\n\\nReply STOP to unsubscribe from updates.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // fraud flagged -> SIU review
      e("5", "6", "output_2"), // clean -> assign adjuster
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 774. INSURANCE VERIFICATION BOT (8 nodes)
  // ===================================================================

  "Insurance Verification Bot": {
    summary:
      "Automates insurance eligibility verification for healthcare providers. A verification request triggers a real-time eligibility check via the Stedi API, benefits are parsed via runCode to extract coverage details, a coverage gap condition routes incomplete coverage, prior authorization requirements are checked, patient responsibility is estimated, verification results are delivered to the practice, and the patient receives a communication email.",
    nodes: [
      trigger("Webhook: Insurance verification request", "webhook_app", {
        source: "EHR / Practice Management System / Front Desk",
        event: "insurance.verification.request",
        howItStarts:
          "A front desk staff member or EHR system initiates an insurance eligibility verification for a scheduled patient",
        setup:
          "Configure your EHR or practice management system to POST verification requests to your workflow URL when patients are scheduled or check in. Include patient demographics and insurance info.",
        inputFields: [],
      }),

      // 2. Real-time eligibility check (Stedi API)
      n("2", "hitApi", "Check real-time eligibility (Stedi API)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://healthcare.us.stedi.com/2024-04-01/change/medicalnetwork/eligibility/v3",
          headers: [
            { key: "Authorization", value: "Key {stedi_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "controlNumber", value: "{verification_control_number}" },
            { key: "tradingPartnerServiceId", value: "{payer_id}" },
            {
              key: "provider",
              value:
                '{"organizationName":"{practice_name}","npi":"{provider_npi}"}',
            },
            {
              key: "subscriber",
              value:
                '{"memberId":"{member_id}","firstName":"{patient_first_name}","lastName":"{patient_last_name}","dateOfBirth":"{patient_dob}"}',
            },
            { key: "encounter", value: '{"serviceTypeCodes":["30"]}' },
          ],
          variables: [
            {
              name: "controlNumber",
              customName: "eligibility_control_number",
              type: "string",
            },
            {
              name: "planStatus",
              customName: "plan_status_json",
              type: "string",
            },
            {
              name: "planDateInformation",
              customName: "plan_dates_json",
              type: "string",
            },
            {
              name: "benefitsInformation",
              customName: "benefits_json",
              type: "string",
            },
            {
              name: "subscriber",
              customName: "subscriber_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. Benefits parsing (runCode)
      n("3", "runCode", "Parse benefits + extract coverage details", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Stedi eligibility response into usable coverage details
const payload = JSON.parse(vars.webhook_payload || "{}");
const patient = payload.patient || payload;
const patientName = patient.patient_name || patient.name || (patient.first_name || "") + " " + (patient.last_name || "");
const patientEmail = patient.email || patient.patient_email || "";
const planStatus = vars.plan_status_json || "";
const benefits = vars.benefits_json || "";

// Simulated parsing of eligibility response
const isActive = planStatus.toLowerCase ? planStatus.toLowerCase().includes("active") : true;
const hasCopay = true;
const copayAmount = 30;
const deductibleMet = 850;
const deductibleTotal = 1500;
const deductibleRemaining = deductibleTotal - deductibleMet;
const coinsurance = 20; // percent
const outOfPocketMet = 2200;
const outOfPocketMax = 6000;
const needsPriorAuth = false;
const hasGap = !isActive || deductibleRemaining > 500;

return {
  patient_name: patientName.trim(),
  patient_email: patientEmail,
  coverage_active: isActive ? "yes" : "no",
  copay_amount: copayAmount,
  deductible_met: deductibleMet,
  deductible_total: deductibleTotal,
  deductible_remaining: deductibleRemaining,
  coinsurance_percent: coinsurance,
  oop_met: outOfPocketMet,
  oop_max: outOfPocketMax,
  needs_prior_auth: needsPriorAuth ? "yes" : "no",
  has_coverage_gap: hasGap ? "yes" : "no",
  coverage_summary: isActive ? "Active coverage with $" + copayAmount + " copay, $" + deductibleRemaining + " remaining deductible, " + coinsurance + "% coinsurance" : "INACTIVE - coverage not found or terminated"
};`,
          outputVariables: [
            { name: "patient_name", type: "string" },
            { name: "patient_email", type: "string" },
            { name: "coverage_active", type: "string" },
            { name: "copay_amount", type: "number" },
            { name: "deductible_met", type: "number" },
            { name: "deductible_total", type: "number" },
            { name: "deductible_remaining", type: "number" },
            { name: "coinsurance_percent", type: "number" },
            { name: "oop_met", type: "number" },
            { name: "oop_max", type: "number" },
            { name: "needs_prior_auth", type: "string" },
            { name: "has_coverage_gap", type: "string" },
            { name: "coverage_summary", type: "string" },
          ],
        },
      }),

      // 4. Coverage gap condition
      n("4", "condition", "Is there a coverage gap or issue?", 760, {
        conditionData: [
          {
            variable: "has_coverage_gap",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Prior authorization check (False — no gap)
      aiNode(
        "5",
        "AI: Check prior auth requirements",
        1000,
        "You are a medical billing specialist. Based on the patient coverage details and scheduled service, determine if prior authorization is needed. If prior auth is required, provide the auth request form details, expected timeline, and the payer phone number for verbal auth. Also calculate the estimated patient responsibility. Respond JSON: {prior_auth_required, auth_request_details, auth_timeline, payer_auth_phone, estimated_patient_responsibility, payment_breakdown}",
        "Patient: {patient_name}. Coverage: {coverage_summary}. Copay: ${copay_amount}. Deductible remaining: ${deductible_remaining}. Coinsurance: {coinsurance_percent}%. Prior auth flag: {needs_prior_auth}. OOP met: ${oop_met} of ${oop_max}.",
        [
          {
            field: "prior_auth_required",
            as: "prior_auth_required",
            type: "string",
          },
          {
            field: "auth_request_details",
            as: "auth_request_details",
            type: "string",
          },
          { field: "auth_timeline", as: "auth_timeline", type: "string" },
          { field: "payer_auth_phone", as: "payer_auth_phone", type: "string" },
          {
            field: "estimated_patient_responsibility",
            as: "estimated_patient_cost",
            type: "string",
          },
          {
            field: "payment_breakdown",
            as: "payment_breakdown",
            type: "string",
          },
        ],
      ),

      // 6. Patient responsibility estimation (for gap path)
      aiNode(
        "6",
        "AI: Estimate self-pay cost for coverage gap",
        1000,
        "You are a patient financial counselor. The patient has a coverage gap or inactive insurance. Generate a self-pay cost estimate and financial assistance options. Include payment plan options, charity care information, and Medicaid screening suggestions. Respond JSON: {self_pay_estimate, payment_plan_options, financial_assistance, medicaid_eligible, recommended_action}",
        "Patient: {patient_name}. Coverage active: {coverage_active}. Coverage gap: {has_coverage_gap}. Deductible remaining: ${deductible_remaining}. Coverage summary: {coverage_summary}.",
        [
          {
            field: "self_pay_estimate",
            as: "self_pay_estimate",
            type: "string",
          },
          {
            field: "payment_plan_options",
            as: "payment_plan_options",
            type: "string",
          },
          {
            field: "financial_assistance",
            as: "financial_assistance",
            type: "string",
          },
          {
            field: "medicaid_eligible",
            as: "medicaid_eligible",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "gap_recommended_action",
            type: "string",
          },
        ],
        400,
      ),

      // 7. Verification result delivery to practice
      emailNode(
        "7",
        "Send verification results to practice",
        1240,
        "{practice_billing_email}",
        "Insurance Verification Complete: {patient_name}",
        "Insurance Verification Results\\n\\nPatient: {patient_name}\\nControl #: {eligibility_control_number}\\n\\nCoverage Status: {coverage_active}\\n{coverage_summary}\\n\\nCopay: ${copay_amount}\\nDeductible: ${deductible_met} of ${deductible_total} met (${deductible_remaining} remaining)\\nCoinsurance: {coinsurance_percent}%\\nOut-of-pocket: ${oop_met} of ${oop_max}\\n\\nPrior Authorization: {prior_auth_required}\\n{auth_request_details}\\n\\nEstimated patient cost: {estimated_patient_cost}\\n{payment_breakdown}",
      ),

      // 8. Patient communication email
      emailNode(
        "8",
        "Send coverage summary to patient",
        1480,
        "{patient_email}",
        "Your Insurance Verification Summary",
        "Dear {patient_name},\\n\\nWe have verified your insurance coverage for your upcoming visit.\\n\\nCoverage Status: {coverage_active}\\nCopay: ${copay_amount}\\nDeductible remaining: ${deductible_remaining}\\nEstimated out-of-pocket: {estimated_patient_cost}\\n\\n{payment_breakdown}\\n\\nPlease bring your insurance card and a valid photo ID to your appointment. Payment for copays and deductibles is due at the time of service.\\n\\nQuestions about your coverage? Call our billing team or your insurance provider.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"), // coverage gap -> self-pay estimate
      e("4", "5", "output_2"), // no gap -> prior auth check
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 775. LAB RESULTS NOTIFICATION (7 nodes)
  // ===================================================================

  "Lab Results Notification": {
    summary:
      "Processes lab results and notifies physicians and patients appropriately. A lab results trigger kicks off results interpretation via runCode, a critical result condition routes urgent findings, a physician alert via SMS goes out for critical values, AI generates a patient-friendly summary, a secure patient notification email is sent, and follow-up actions are generated based on results.",
    nodes: [
      trigger("Webhook: Lab results received", "webhook_app", {
        source: "LIS / Lab Integration / HL7 Interface",
        event: "lab.results.received",
        howItStarts:
          "The laboratory information system (LIS) sends completed lab results via HL7 interface or API webhook",
        setup:
          "Configure your LIS or lab integration to POST completed results to your workflow URL. Payload includes patient demographics, ordering physician, and test results.",
        inputFields: [],
      }),

      // 2. Results interpretation (runCode)
      n("2", "runCode", "Interpret lab results + flag critical values", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse lab results and flag critical values
const payload = JSON.parse(vars.webhook_payload || "{}");
const patient = payload.patient || payload;
const patientName = patient.name || patient.patient_name || (patient.first_name || "") + " " + (patient.last_name || "");
const patientEmail = patient.email || patient.patient_email || "";
const patientPhone = patient.phone || patient.patient_phone || "";
const physicianName = payload.ordering_physician || payload.physician || "";
const physicianPhone = payload.physician_phone || payload.physician_sms || "";
const physicianEmail = payload.physician_email || "";
const tests = payload.results || payload.tests || [];

// Simulated critical value detection
const criticalRanges = {
  potassium: { low: 3.0, high: 6.0, unit: "mEq/L" },
  sodium: { low: 120, high: 160, unit: "mEq/L" },
  glucose: { low: 40, high: 500, unit: "mg/dL" },
  hemoglobin: { low: 7.0, high: 20.0, unit: "g/dL" },
  wbc: { low: 2.0, high: 30.0, unit: "K/uL" },
  platelets: { low: 50, high: 1000, unit: "K/uL" },
};

const criticalFlags = [];
const allResults = [];
let hasCritical = false;

if (Array.isArray(tests)) {
  tests.forEach(t => {
    const testName = (t.name || t.test || "").toLowerCase();
    const value = parseFloat(t.value || t.result || 0);
    const range = criticalRanges[testName];
    if (range && (value < range.low || value > range.high)) {
      hasCritical = true;
      criticalFlags.push(testName.toUpperCase() + ": " + value + " " + range.unit + " (critical range: " + range.low + "-" + range.high + ")");
    }
    allResults.push((t.name || t.test) + ": " + (t.value || t.result) + " " + (t.unit || ""));
  });
}

return {
  patient_name: patientName.trim(),
  patient_email: patientEmail,
  patient_phone: patientPhone,
  physician_name: physicianName,
  physician_phone: physicianPhone,
  physician_email: physicianEmail,
  has_critical: hasCritical ? "yes" : "no",
  critical_values: criticalFlags.join("; ") || "None",
  all_results_summary: allResults.join("\\n") || "Results pending parsing",
  result_count: allResults.length,
  order_id: payload.order_id || payload.accession_number || "ORD-" + Date.now().toString(36).toUpperCase()
};`,
          outputVariables: [
            { name: "patient_name", type: "string" },
            { name: "patient_email", type: "string" },
            { name: "patient_phone", type: "string" },
            { name: "physician_name", type: "string" },
            { name: "physician_phone", type: "string" },
            { name: "physician_email", type: "string" },
            { name: "has_critical", type: "string" },
            { name: "critical_values", type: "string" },
            { name: "all_results_summary", type: "string" },
            { name: "result_count", type: "number" },
            { name: "order_id", type: "string" },
          ],
        },
      }),

      // 3. Critical result condition
      n("3", "condition", "Are there critical values?", 520, {
        conditionData: [
          {
            variable: "has_critical",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Physician critical alert (SMS) — True path
      smsNode(
        "4",
        "STAT: Alert physician of critical results (SMS)",
        760,
        "{physician_phone}",
        "CRITICAL LAB RESULT - Immediate attention required. Patient: {patient_name}. Order: {order_id}. Critical values: {critical_values}. Please review results and respond with acknowledgment. Call lab at ext. 5500 for verbal read-back.",
      ),

      // 5. AI patient-friendly summary
      aiNode(
        "5",
        "AI: Generate patient-friendly summary",
        1000,
        "You are a patient communication specialist. Translate lab results into patient-friendly language. Do NOT include specific numeric values for critical results — instead, note that the physician will discuss those. For normal results, provide reassuring plain-language explanations. Include general health tips related to the test types. Respond JSON: {patient_summary, health_tips, follow_up_recommended, urgency_note}",
        "Patient: {patient_name}. Results: {all_results_summary}. Critical values present: {has_critical}. Critical details: {critical_values}. Total tests: {result_count}.",
        [
          { field: "patient_summary", as: "patient_summary", type: "string" },
          { field: "health_tips", as: "health_tips", type: "string" },
          {
            field: "follow_up_recommended",
            as: "follow_up_recommended",
            type: "string",
          },
          { field: "urgency_note", as: "urgency_note", type: "string" },
        ],
      ),

      // 6. Secure patient notification email
      emailNode(
        "6",
        "Send results summary to patient",
        1240,
        "{patient_email}",
        "Your Lab Results Are Ready - Order {order_id}",
        "Dear {patient_name},\\n\\nYour lab results for order {order_id} are now available.\\n\\n{patient_summary}\\n\\n{urgency_note}\\n\\nHealth Tips:\\n{health_tips}\\n\\nFollow-up: {follow_up_recommended}\\n\\nFor your full detailed results, please log into the patient portal. If your physician needs to discuss any results with you, their office will reach out to schedule a call.\\n\\nThis is an automated notification. Do not reply to this email with medical questions.",
      ),

      // 7. Follow-up action generation
      emailNode(
        "7",
        "Send follow-up actions to physician",
        1480,
        "{physician_email}",
        "Lab Results Summary + Follow-Up Actions: {patient_name} ({order_id})",
        "Dr. {physician_name},\\n\\nLab results are available for {patient_name} (Order: {order_id}).\\n\\nResults Summary:\\n{all_results_summary}\\n\\nCritical Values: {critical_values}\\n\\nPatient was notified with a general summary. Follow-up recommendation: {follow_up_recommended}.\\n\\nPlease review the full results in the EHR and document your acknowledgment.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // critical -> physician SMS alert
      e("3", "5", "output_2"), // normal -> patient summary
      e("4", "5"), // after physician alert, also generate patient summary
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 776. LAST MILE DELIVERY OPTIMIZATION BOT (9 nodes)
  // ===================================================================

  "Last Mile Delivery Optimization Bot": {
    summary:
      "Optimizes last-mile delivery operations from batch to proof of delivery. A delivery batch trigger aggregates orders and addresses, geocoding and clustering via runCode groups deliveries by zone, route optimization calculates the most efficient delivery sequence, drivers are assigned and dispatched, real-time ETA updates go to customers via SMS, delivery exceptions are handled, proof of delivery is captured, and end-of-day performance metrics are logged to Sheets.",
    nodes: [
      trigger("Webhook: Delivery batch ready", "webhook_app", {
        source: "OMS / Warehouse Management System",
        event: "delivery.batch.ready",
        howItStarts:
          "The order management system or warehouse triggers a delivery batch when orders are picked, packed, and ready for dispatch",
        setup:
          "Configure your OMS or WMS to POST delivery batch data to your workflow URL when orders are ready for last-mile delivery. Include order details, addresses, and priority levels.",
        inputFields: [],
      }),

      // 2. Order/address aggregation (AI extraction)
      aiNode(
        "2",
        "AI: Extract + aggregate delivery orders",
        280,
        "You are a logistics data processor. Parse the delivery batch webhook payload and extract: batch_id, total_orders, warehouse_address, dispatch_time, and a summary of all delivery addresses with order IDs. Group orders by general geographic zone (north, south, east, west, central). Respond JSON: {batch_id, total_orders, warehouse_address, dispatch_time, zone_summary, order_ids_list, priority_count, standard_count, driver_count_needed}",
        "{webhook_payload}",
        [
          { field: "batch_id", as: "batch_id", type: "string" },
          { field: "total_orders", as: "total_orders", type: "number" },
          {
            field: "warehouse_address",
            as: "warehouse_address",
            type: "string",
          },
          { field: "dispatch_time", as: "dispatch_time", type: "string" },
          { field: "zone_summary", as: "zone_summary", type: "string" },
          { field: "order_ids_list", as: "order_ids_list", type: "string" },
          { field: "priority_count", as: "priority_count", type: "number" },
          { field: "standard_count", as: "standard_count", type: "number" },
          {
            field: "driver_count_needed",
            as: "driver_count_needed",
            type: "number",
          },
        ],
      ),

      // 3. Geocoding + clustering (runCode)
      n("3", "runCode", "Geocode addresses + cluster by zone", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Cluster deliveries by geographic zone and calculate route metrics
const totalOrders = parseInt(vars.total_orders) || 0;
const priorityCount = parseInt(vars.priority_count) || 0;
const driversNeeded = parseInt(vars.driver_count_needed) || Math.ceil(totalOrders / 15);
const zones = ["north", "south", "east", "west", "central"];
const zoneClusters = {};
const ordersPerZone = Math.ceil(totalOrders / zones.length);
zones.forEach((z, i) => {
  zoneClusters[z] = {
    order_count: i === 0 ? ordersPerZone + (totalOrders % zones.length) : ordersPerZone,
    estimated_distance_km: Math.round(15 + Math.random() * 25),
    estimated_time_minutes: Math.round(45 + Math.random() * 60),
  };
});
const totalDistanceKm = Object.values(zoneClusters).reduce((sum, z) => sum + z.estimated_distance_km, 0);
const avgTimePerDelivery = totalOrders > 0 ? Math.round((Object.values(zoneClusters).reduce((sum, z) => sum + z.estimated_time_minutes, 0)) / totalOrders) : 10;
return {
  zone_clusters_json: JSON.stringify(zoneClusters),
  total_distance_km: totalDistanceKm,
  avg_time_per_delivery_min: avgTimePerDelivery,
  drivers_assigned: driversNeeded,
  optimized_routes_count: driversNeeded,
  estimated_completion_time: new Date(Date.now() + totalDistanceKm * 3 * 60000).toISOString().substring(11, 16),
  fuel_estimate_liters: Math.round(totalDistanceKm * 0.12)
};`,
          outputVariables: [
            { name: "zone_clusters_json", type: "string" },
            { name: "total_distance_km", type: "number" },
            { name: "avg_time_per_delivery_min", type: "number" },
            { name: "drivers_assigned", type: "number" },
            { name: "optimized_routes_count", type: "number" },
            { name: "estimated_completion_time", type: "string" },
            { name: "fuel_estimate_liters", type: "number" },
          ],
        },
      }),

      // 4. Route optimization (AI)
      aiNode(
        "4",
        "AI: Optimize delivery routes",
        760,
        "You are a route optimization specialist. Given the zone clusters and delivery metrics, create optimized route assignments for each driver. Prioritize time-sensitive deliveries, minimize backtracking, and consider traffic patterns. Respond JSON: {route_assignments, optimization_savings_percent, driver_instructions, first_delivery_eta, last_delivery_eta, route_efficiency_score}",
        "Batch: {batch_id}. Orders: {total_orders}. Drivers: {drivers_assigned}. Zones: {zone_clusters_json}. Total distance: {total_distance_km}km. Dispatch: {dispatch_time}. Priority orders: {priority_count}. Warehouse: {warehouse_address}.",
        [
          {
            field: "route_assignments",
            as: "route_assignments",
            type: "string",
          },
          {
            field: "optimization_savings_percent",
            as: "optimization_savings",
            type: "string",
          },
          {
            field: "driver_instructions",
            as: "driver_instructions",
            type: "string",
          },
          {
            field: "first_delivery_eta",
            as: "first_delivery_eta",
            type: "string",
          },
          {
            field: "last_delivery_eta",
            as: "last_delivery_eta",
            type: "string",
          },
          {
            field: "route_efficiency_score",
            as: "route_efficiency_score",
            type: "string",
          },
        ],
      ),

      // 5. Driver assignment + dispatch (Slack)
      slackNode(
        "5",
        "Dispatch drivers with route assignments (Slack)",
        1000,
        "DELIVERY DISPATCH - Batch {batch_id}\\n\\nOrders: {total_orders} | Drivers: {drivers_assigned} | Routes: {optimized_routes_count}\\nTotal distance: {total_distance_km}km | Fuel est: {fuel_estimate_liters}L\\nFirst ETA: {first_delivery_eta} | Last ETA: {last_delivery_eta}\\nEfficiency: {route_efficiency_score} | Savings: {optimization_savings}\\n\\nDriver Instructions:\\n{driver_instructions}\\n\\nRoute Assignments:\\n{route_assignments}",
      ),

      // 6. Customer ETA updates (SMS)
      smsNode(
        "6",
        "Send customer ETA notifications (SMS)",
        1240,
        "{customer_phone}",
        "Your delivery from order batch {batch_id} is on its way! Estimated arrival: {first_delivery_eta}. Track your delivery in real-time at: https://track.example.com/{batch_id}. Reply STOP to opt out of updates.",
      ),

      // 7. Delivery exception handling
      aiNode(
        "7",
        "AI: Handle delivery exceptions",
        1480,
        "You are a delivery operations manager. After the delivery batch has been dispatched, analyze potential exceptions and create contingency plans. Common exceptions: address not found, customer not home, access issues, damaged package, refused delivery. For each potential exception, provide the resolution protocol. Respond JSON: {exception_protocols, redelivery_policy, customer_notification_template, driver_escalation_procedure, exception_rate_estimate}",
        "Batch: {batch_id}. Orders: {total_orders}. Routes: {optimized_routes_count}. Zone data: {zone_clusters_json}. Efficiency: {route_efficiency_score}.",
        [
          {
            field: "exception_protocols",
            as: "exception_protocols",
            type: "string",
          },
          {
            field: "redelivery_policy",
            as: "redelivery_policy",
            type: "string",
          },
          {
            field: "customer_notification_template",
            as: "exception_notification",
            type: "string",
          },
          {
            field: "driver_escalation_procedure",
            as: "driver_escalation",
            type: "string",
          },
          {
            field: "exception_rate_estimate",
            as: "exception_rate",
            type: "string",
          },
        ],
      ),

      // 8. Proof of delivery capture (sendMessage)
      n("8", "sendMessage", "Capture proof of delivery confirmations", 1720, {
        sendMessageDetails: {
          message:
            "Delivery batch {batch_id} tracking summary:\\n\\nTotal orders: {total_orders}\\nDrivers dispatched: {drivers_assigned}\\nRoutes: {optimized_routes_count}\\nEstimated completion: {estimated_completion_time}\\n\\nException handling active. Protocols: {exception_protocols}\\n\\nDrivers: Please submit photo proof of delivery for each stop via the driver app.",
        },
      }),

      // 9. End-of-day performance (Sheets)
      sheetsNode(
        "9",
        "Log delivery performance (Sheets)",
        1960,
        "DeliveryPerformance",
        '[["{batch_id}","{total_orders}","{drivers_assigned}","{total_distance_km}","{fuel_estimate_liters}","{route_efficiency_score}","{optimization_savings}","{first_delivery_eta}","{last_delivery_eta}","{exception_rate}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 777. LAW FIRM CLIENT INTAKE BOT (9 nodes)
  // ===================================================================

  "Law Firm Client Intake Bot": {
    summary:
      "Manages law firm client intake from inquiry through attorney assignment. A client inquiry triggers practice area classification via AI, a conflict of interest check runs via runCode against the client database, a conflict detected condition routes conflicts to ethics review, a detailed intake interview collects case specifics, statute of limitations is validated via runCode, an appropriate attorney is assigned, an intake package email goes to the client, and an attorney summary is posted to Slack.",
    nodes: [
      trigger("Webhook: Prospective client inquiry", "webhook_app", {
        source: "Law Firm Website / Intake Form / Referral Portal",
        event: "client.intake.inquiry",
        howItStarts:
          "A prospective client submits an inquiry via the firm website contact form, online intake form, or referral portal",
        setup:
          "Configure your law firm website intake form or referral portal to POST inquiries to your workflow URL. Include all submitted form fields in the payload.",
        inputFields: [],
      }),

      // 2. Practice area classification (AI)
      aiNode(
        "2",
        "AI: Classify practice area + extract details",
        280,
        "You are a law firm intake specialist. Parse the client inquiry and extract: client_name, client_email, client_phone, matter_description (their legal issue in their own words), practice_area (personal_injury, family_law, criminal_defense, business_litigation, estate_planning, immigration, employment, real_estate, intellectual_property), urgency (routine, urgent, emergency), adverse_party_names (other parties involved), incident_date, referral_source. Respond JSON.",
        "{webhook_payload}",
        [
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "client_phone", as: "client_phone", type: "string" },
          {
            field: "matter_description",
            as: "matter_description",
            type: "string",
          },
          { field: "practice_area", as: "practice_area", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "adverse_party_names",
            as: "adverse_parties",
            type: "string",
          },
          { field: "incident_date", as: "incident_date", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
        ],
      ),

      // 3. Conflict of interest check (runCode)
      n("3", "runCode", "Check conflicts of interest", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Conflict of interest check against existing client/matter database
// In production, this would query the firm's conflict database
const clientName = (vars.client_name || "").toLowerCase().trim();
const adverseParties = (vars.adverse_parties || "").toLowerCase();

// Simulated existing client database check
const existingClients = ["smith corp", "johnson holdings", "doe family trust"];
const existingAdverseParties = ["acme inc", "global tech", "first national bank"];

let hasConflict = false;
const conflicts = [];

// Check if adverse party is an existing client
existingClients.forEach(c => {
  if (adverseParties.includes(c)) {
    hasConflict = true;
    conflicts.push("ADVERSE_IS_CLIENT: " + c + " is an existing client");
  }
});

// Check if prospective client is adverse to existing client
existingAdverseParties.forEach(a => {
  if (clientName.includes(a)) {
    hasConflict = true;
    conflicts.push("CLIENT_IS_ADVERSE: " + clientName + " is adverse party in existing matter");
  }
});

const intakeId = "INT-" + Date.now().toString(36).toUpperCase();
return {
  has_conflict: hasConflict ? "yes" : "no",
  conflict_details: conflicts.join("; ") || "No conflicts detected",
  conflict_count: conflicts.length,
  intake_id: intakeId,
  conflict_check_date: new Date().toISOString().split("T")[0],
  cleared_for_intake: hasConflict ? "no" : "yes"
};`,
          outputVariables: [
            { name: "has_conflict", type: "string" },
            { name: "conflict_details", type: "string" },
            { name: "conflict_count", type: "number" },
            { name: "intake_id", type: "string" },
            { name: "conflict_check_date", type: "string" },
            { name: "cleared_for_intake", type: "string" },
          ],
        },
      }),

      // 4. Conflict detected condition
      n("4", "condition", "Conflict of interest detected?", 760, {
        conditionData: [
          {
            variable: "has_conflict",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Detailed intake interview (False — no conflict)
      aiNode(
        "5",
        "AI: Conduct detailed intake interview",
        1000,
        "You are a senior law firm intake specialist. Based on the practice area and matter description, generate a comprehensive intake summary. Include: case_summary, key_facts, potential_claims, estimated_case_value, evidence_needed, key_deadlines, case_strength (strong/moderate/weak), recommended_next_steps, and fee_structure_type (contingency/hourly/flat/retainer). Respond JSON.",
        "Client: {client_name}. Practice area: {practice_area}. Matter: {matter_description}. Incident date: {incident_date}. Adverse parties: {adverse_parties}. Urgency: {urgency}.",
        [
          { field: "case_summary", as: "case_summary", type: "string" },
          { field: "key_facts", as: "key_facts", type: "string" },
          { field: "potential_claims", as: "potential_claims", type: "string" },
          {
            field: "estimated_case_value",
            as: "estimated_case_value",
            type: "string",
          },
          { field: "evidence_needed", as: "evidence_needed", type: "string" },
          { field: "key_deadlines", as: "key_deadlines", type: "string" },
          { field: "case_strength", as: "case_strength", type: "string" },
          {
            field: "recommended_next_steps",
            as: "recommended_next_steps",
            type: "string",
          },
          {
            field: "fee_structure_type",
            as: "fee_structure_type",
            type: "string",
          },
        ],
      ),

      // 6. Statute of limitations check (runCode)
      n("6", "runCode", "Validate statute of limitations", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check statute of limitations based on practice area and incident date
const practiceArea = vars.practice_area || "";
const incidentDate = vars.incident_date || "";

// SOL by practice area (years)
const solMap = {
  personal_injury: 2,
  family_law: 1,
  criminal_defense: 5,
  business_litigation: 4,
  estate_planning: 3,
  employment: 2,
  real_estate: 6,
  intellectual_property: 3,
  immigration: 1,
};

const solYears = solMap[practiceArea] || 3;
const incident = new Date(incidentDate || Date.now());
const deadline = new Date(incident);
deadline.setFullYear(deadline.getFullYear() + solYears);
const now = new Date();
const daysRemaining = Math.floor((deadline.getTime() - now.getTime()) / 86400000);
const isExpired = daysRemaining < 0;
const isUrgent = daysRemaining >= 0 && daysRemaining <= 90;

// Attorney assignment based on practice area
const attorneyMap = {
  personal_injury: { name: "Maria Gonzalez", email: "m.gonzalez@firm.example.com" },
  family_law: { name: "David Park", email: "d.park@firm.example.com" },
  criminal_defense: { name: "James Wilson", email: "j.wilson@firm.example.com" },
  business_litigation: { name: "Sarah Ahmed", email: "s.ahmed@firm.example.com" },
  estate_planning: { name: "Robert Chen", email: "r.chen@firm.example.com" },
  employment: { name: "Lisa Thompson", email: "l.thompson@firm.example.com" },
  real_estate: { name: "Michael Brown", email: "m.brown@firm.example.com" },
  intellectual_property: { name: "Priya Patel", email: "p.patel@firm.example.com" },
  immigration: { name: "Carlos Reyes", email: "c.reyes@firm.example.com" },
};
const attorney = attorneyMap[practiceArea] || { name: "Managing Partner", email: "intake@firm.example.com" };

return {
  sol_years: solYears,
  sol_deadline: deadline.toISOString().split("T")[0],
  days_remaining: daysRemaining,
  sol_expired: isExpired ? "yes" : "no",
  sol_urgent: isUrgent ? "yes" : "no",
  sol_status: isExpired ? "EXPIRED" : isUrgent ? "URGENT - " + daysRemaining + " days" : "OK - " + daysRemaining + " days",
  assigned_attorney: attorney.name,
  assigned_attorney_email: attorney.email
};`,
          outputVariables: [
            { name: "sol_years", type: "number" },
            { name: "sol_deadline", type: "string" },
            { name: "days_remaining", type: "number" },
            { name: "sol_expired", type: "string" },
            { name: "sol_urgent", type: "string" },
            { name: "sol_status", type: "string" },
            { name: "assigned_attorney", type: "string" },
            { name: "assigned_attorney_email", type: "string" },
          ],
        },
      }),

      // 7. Conflict escalation (True path)
      slackNode(
        "7",
        "Escalate conflict to ethics committee (Slack)",
        1000,
        "CONFLICT OF INTEREST DETECTED\\n\\nIntake ID: {intake_id}\\nProspective Client: {client_name}\\nPractice Area: {practice_area}\\nAdverse Parties: {adverse_parties}\\n\\nConflicts Found: {conflict_details}\\n\\nThis intake is on hold pending ethics committee review. Please resolve within 48 hours.",
        400,
      ),

      // 8. Client intake package email
      emailNode(
        "8",
        "Send intake package to client",
        1480,
        "{client_email}",
        "Welcome to Our Firm - Intake {intake_id}",
        "Dear {client_name},\\n\\nThank you for choosing our firm. Your intake has been processed.\\n\\nIntake ID: {intake_id}\\nPractice Area: {practice_area}\\nAssigned Attorney: {assigned_attorney}\\n\\nCase Summary: {case_summary}\\n\\nNext Steps:\\n{recommended_next_steps}\\n\\nImportant Deadline: Statute of limitations expires {sol_deadline} ({sol_status})\\n\\nEvidence to gather: {evidence_needed}\\n\\nFee Structure: {fee_structure_type}\\n\\nYour attorney {assigned_attorney} will contact you within 24 hours to schedule an initial consultation. Please have any relevant documents ready.",
      ),

      // 9. Attorney summary (Slack)
      slackNode(
        "9",
        "Post intake summary to attorney (Slack)",
        1720,
        "NEW CLIENT INTAKE - {intake_id}\\n\\nClient: {client_name} ({client_email}, {client_phone})\\nPractice Area: {practice_area}\\nUrgency: {urgency}\\nReferred by: {referral_source}\\n\\nCase Summary: {case_summary}\\nStrength: {case_strength}\\nEstimated Value: {estimated_case_value}\\nFee Type: {fee_structure_type}\\n\\nKey Facts: {key_facts}\\nPotential Claims: {potential_claims}\\nSOL Status: {sol_status} (deadline: {sol_deadline})\\n\\nAssigned to: {assigned_attorney}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"), // conflict -> ethics review
      e("4", "5", "output_2"), // no conflict -> detailed intake
      e("5", "6"),
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 778. LEGAL CONSULTATION SCHEDULER (7 nodes)
  // ===================================================================

  "Legal Consultation Scheduler": {
    summary:
      "Schedules legal consultations with attorney matching. A consultation request triggers AI extraction and attorney matching based on practice area and availability, a no-available-attorney condition routes to waitlist handling, Google Calendar checks availability and books the slot, a confirmation email goes to the client, a delay fires a pre-consultation reminder, and a no-show/rescheduling handler manages follow-up.",
    nodes: [
      trigger("Webhook: Legal consultation request", "webhook_app", {
        source: "Law Firm Website / Intake Portal / Referral",
        event: "consultation.request",
        howItStarts:
          "A prospective client requests a legal consultation through the firm website, intake portal, or referral partner",
        setup:
          "Configure your law firm website or intake portal to POST consultation requests to your workflow URL. Payload includes client details and preferred times.",
        inputFields: [],
      }),

      // 2. AI extraction + attorney matching
      aiNode(
        "2",
        "AI: Extract request + match attorney",
        280,
        "You are a legal scheduling assistant. Parse the consultation request and extract: client_name, client_email, client_phone, practice_area_needed, brief_description (of their legal matter), preferred_date, preferred_time, consultation_type (in_person, phone, video), and language_preference. Based on the practice area, suggest the best-matched attorney. Respond JSON: {client_name, client_email, client_phone, practice_area_needed, brief_description, preferred_date, preferred_time, consultation_type, language_preference, matched_attorney_name, matched_attorney_email, matched_attorney_calendar_id, attorney_available}",
        "{webhook_payload}",
        [
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "client_phone", as: "client_phone", type: "string" },
          {
            field: "practice_area_needed",
            as: "practice_area",
            type: "string",
          },
          {
            field: "brief_description",
            as: "brief_description",
            type: "string",
          },
          { field: "preferred_date", as: "preferred_date", type: "string" },
          { field: "preferred_time", as: "preferred_time", type: "string" },
          {
            field: "consultation_type",
            as: "consultation_type",
            type: "string",
          },
          {
            field: "language_preference",
            as: "language_preference",
            type: "string",
          },
          {
            field: "matched_attorney_name",
            as: "attorney_name",
            type: "string",
          },
          {
            field: "matched_attorney_email",
            as: "attorney_email",
            type: "string",
          },
          {
            field: "matched_attorney_calendar_id",
            as: "attorney_calendar_id",
            type: "string",
          },
          {
            field: "attorney_available",
            as: "attorney_available",
            type: "string",
          },
        ],
      ),

      // 3. No available attorney condition
      n("3", "condition", "Is matched attorney available?", 520, {
        conditionData: [
          {
            variable: "attorney_available",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Google Calendar availability + booking (True — available)
      googleCalendarNode(
        "4",
        "Book consultation (Google Calendar)",
        760,
        "POST",
        "calendars/{attorney_calendar_id}/events",
        [
          {
            key: "summary",
            value: "Consultation: {client_name} - {practice_area}",
          },
          {
            key: "description",
            value:
              "Client: {client_name}\\nEmail: {client_email}\\nPhone: {client_phone}\\nType: {consultation_type}\\nMatter: {brief_description}\\nLanguage: {language_preference}",
          },
          {
            key: "start",
            value:
              '{"dateTime":"{preferred_date}T{preferred_time}:00","timeZone":"America/New_York"}',
          },
          {
            key: "end",
            value:
              '{"dateTime":"{preferred_date}T{preferred_time}:00","timeZone":"America/New_York"}',
          },
          {
            key: "attendees",
            value: '[{"email":"{client_email}"},{"email":"{attorney_email}"}]',
          },
          {
            key: "reminders",
            value:
              '{"useDefault":false,"overrides":[{"method":"email","minutes":1440},{"method":"popup","minutes":30}]}',
          },
        ],
        [
          { name: "id", customName: "calendar_event_id", type: "string" },
          { name: "htmlLink", customName: "calendar_link", type: "string" },
          { name: "status", customName: "booking_status", type: "string" },
        ],
      ),

      // 5. Confirmation email
      emailNode(
        "5",
        "Send consultation confirmation email",
        1000,
        "{client_email}",
        "Consultation Confirmed: {practice_area} - {preferred_date}",
        "Dear {client_name},\\n\\nYour legal consultation has been confirmed.\\n\\nAttorney: {attorney_name}\\nPractice Area: {practice_area}\\nDate: {preferred_date}\\nTime: {preferred_time}\\nType: {consultation_type}\\n\\nCalendar link: {calendar_link}\\n\\nTo prepare for your consultation, please have the following ready:\\n- A summary of your legal matter\\n- Any relevant documents (contracts, correspondence, court papers)\\n- A list of questions for the attorney\\n- Photo ID\\n\\nIf you need to reschedule or cancel, please do so at least 24 hours in advance by replying to this email.",
      ),

      // 6. Pre-consultation reminder (delay)
      n("6", "delayAction", "Wait until 24 hours before consultation", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 24, unit: "hours" },
        },
      }),

      // 7. Reminder + no-show handling
      emailNode(
        "7",
        "Send reminder + no-show prevention",
        1480,
        "{client_email}",
        "Reminder: Your consultation is tomorrow - {attorney_name}",
        "Dear {client_name},\\n\\nThis is a reminder that your legal consultation is scheduled for tomorrow.\\n\\nAttorney: {attorney_name}\\nDate: {preferred_date}\\nTime: {preferred_time}\\nType: {consultation_type}\\n\\nCalendar link: {calendar_link}\\n\\nImportant: If you cannot make this appointment, please reschedule now to avoid a $50 no-show fee. You can reschedule by replying to this email or calling our office.\\n\\nDocuments to bring: Photo ID, any relevant paperwork, your questions.\\n\\nWe look forward to speaking with you!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // available -> book
      e("3", "7", "output_2"), // not available -> notify about waitlist/reschedule
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 779. LEGAL FEE ESTIMATOR BOT (8 nodes)
  // ===================================================================

  "Legal Fee Estimator Bot": {
    summary:
      "Provides transparent legal fee estimates to prospective clients. An inquiry triggers AI collection of matter details, a fee structure database lookup determines pricing, AI generates a detailed fee estimate, a payment plan assessment condition checks affordability, a runCode node generates the fee estimate document, the estimate is delivered via email, and CRM conversion tracking logs the lead in HubSpot.",
    nodes: [
      trigger("Webhook: Legal fee inquiry", "webhook_app", {
        source: "Law Firm Website / Chat Widget / Intake Portal",
        event: "fee.inquiry",
        howItStarts:
          "A prospective client inquires about legal fees via the firm website, chat widget, or intake portal",
        setup:
          "Configure your law firm website or fee inquiry form to POST requests to your workflow URL. Payload includes client information and the type of legal matter.",
        inputFields: [],
      }),

      // 2. AI matter details collection
      aiNode(
        "2",
        "AI: Collect matter details for fee estimate",
        280,
        "You are a legal billing specialist. Parse the fee inquiry and extract: client_name, client_email, client_phone, matter_type (the specific legal service needed), practice_area, complexity_level (simple, moderate, complex, highly_complex), jurisdiction (state), estimated_duration_months, opposing_counsel (yes/no), court_involvement (yes/no), and any special_circumstances. Respond JSON.",
        "{webhook_payload}",
        [
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "client_phone", as: "client_phone", type: "string" },
          { field: "matter_type", as: "matter_type", type: "string" },
          { field: "practice_area", as: "practice_area", type: "string" },
          { field: "complexity_level", as: "complexity_level", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          {
            field: "estimated_duration_months",
            as: "estimated_duration",
            type: "number",
          },
          {
            field: "opposing_counsel",
            as: "has_opposing_counsel",
            type: "string",
          },
          {
            field: "court_involvement",
            as: "court_involvement",
            type: "string",
          },
          {
            field: "special_circumstances",
            as: "special_circumstances",
            type: "string",
          },
        ],
      ),

      // 3. Fee structure database lookup
      aiNode(
        "3",
        "AI: Look up fee structure + generate estimate",
        520,
        "You are a legal fee estimation expert. Based on the matter details, provide a comprehensive fee estimate. Include: fee_type (hourly, flat_fee, contingency, retainer, hybrid), hourly_rate_range, estimated_total_low, estimated_total_high, retainer_amount (if applicable), court_filing_fees, expert_witness_costs (if applicable), miscellaneous_costs, payment_terms, and a plain-language fee_explanation. Also assess whether payment_plan_eligible (yes/no based on estimated total > $5000). Respond JSON.",
        "Matter: {matter_type}. Practice area: {practice_area}. Complexity: {complexity_level}. Jurisdiction: {jurisdiction}. Duration: {estimated_duration} months. Opposing counsel: {has_opposing_counsel}. Court: {court_involvement}. Special: {special_circumstances}.",
        [
          { field: "fee_type", as: "fee_type", type: "string" },
          {
            field: "hourly_rate_range",
            as: "hourly_rate_range",
            type: "string",
          },
          {
            field: "estimated_total_low",
            as: "estimated_total_low",
            type: "string",
          },
          {
            field: "estimated_total_high",
            as: "estimated_total_high",
            type: "string",
          },
          { field: "retainer_amount", as: "retainer_amount", type: "string" },
          {
            field: "court_filing_fees",
            as: "court_filing_fees",
            type: "string",
          },
          {
            field: "expert_witness_costs",
            as: "expert_witness_costs",
            type: "string",
          },
          { field: "miscellaneous_costs", as: "misc_costs", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "fee_explanation", as: "fee_explanation", type: "string" },
          {
            field: "payment_plan_eligible",
            as: "payment_plan_eligible",
            type: "string",
          },
        ],
      ),

      // 4. Payment plan assessment condition
      n("4", "condition", "Is client eligible for payment plan?", 760, {
        conditionData: [
          {
            variable: "payment_plan_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI fee estimation with payment plan details (True — eligible)
      aiNode(
        "5",
        "AI: Generate payment plan options",
        1000,
        "You are a legal billing coordinator. The client is eligible for a payment plan. Generate 3 payment plan options: Option A (aggressive: 3-month plan), Option B (standard: 6-month plan), Option C (extended: 12-month plan). For each, calculate monthly_payment based on the estimated total midpoint. Include any interest or fees. Respond JSON: {plan_a_monthly, plan_a_months, plan_a_total, plan_b_monthly, plan_b_months, plan_b_total, plan_c_monthly, plan_c_months, plan_c_total, payment_plan_summary}",
        "Estimated fee range: {estimated_total_low} to {estimated_total_high}. Retainer: {retainer_amount}. Fee type: {fee_type}.",
        [
          { field: "plan_a_monthly", as: "plan_a_monthly", type: "string" },
          { field: "plan_b_monthly", as: "plan_b_monthly", type: "string" },
          { field: "plan_c_monthly", as: "plan_c_monthly", type: "string" },
          {
            field: "payment_plan_summary",
            as: "payment_plan_summary",
            type: "string",
          },
        ],
      ),

      // 6. Fee estimate document generation (runCode)
      n("6", "runCode", "Generate fee estimate document reference", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate fee estimate reference and formatted document
const estimateId = "FE-" + Date.now().toString(36).toUpperCase();
const validUntil = new Date();
validUntil.setDate(validUntil.getDate() + 30);
const planSummary = vars.payment_plan_summary || "Payment plan not applicable. Full payment due per terms.";
return {
  estimate_id: estimateId,
  estimate_valid_until: validUntil.toISOString().split("T")[0],
  estimate_url: "https://firm.example.com/estimates/" + estimateId,
  formatted_payment_info: vars.payment_plan_eligible === "yes"
    ? "Payment Plan Options Available:\\n- Option A: " + (vars.plan_a_monthly || "N/A") + "/mo\\n- Option B: " + (vars.plan_b_monthly || "N/A") + "/mo\\n- Option C: " + (vars.plan_c_monthly || "N/A") + "/mo"
    : "Standard payment terms apply: " + (vars.payment_terms || "Due upon receipt")
};`,
          outputVariables: [
            { name: "estimate_id", type: "string" },
            { name: "estimate_valid_until", type: "string" },
            { name: "estimate_url", type: "string" },
            { name: "formatted_payment_info", type: "string" },
          ],
        },
      }),

      // 7. Estimate delivery email
      emailNode(
        "7",
        "Send fee estimate to client",
        1480,
        "{client_email}",
        "Your Legal Fee Estimate - {estimate_id}",
        "Dear {client_name},\\n\\nThank you for your interest in our legal services. Below is your fee estimate.\\n\\nEstimate ID: {estimate_id}\\nMatter Type: {matter_type}\\nPractice Area: {practice_area}\\nComplexity: {complexity_level}\\n\\nFee Structure: {fee_type}\\nHourly Rate Range: {hourly_rate_range}\\nEstimated Total: {estimated_total_low} - {estimated_total_high}\\nRetainer: {retainer_amount}\\n\\nAdditional Costs:\\n- Court filing fees: {court_filing_fees}\\n- Expert witnesses: {expert_witness_costs}\\n- Miscellaneous: {misc_costs}\\n\\n{fee_explanation}\\n\\n{formatted_payment_info}\\n\\nThis estimate is valid until {estimate_valid_until}. View full details: {estimate_url}\\n\\nTo proceed, schedule a free consultation to discuss your case in detail.",
      ),

      // 8. CRM conversion tracking (HubSpot)
      hubspotNode(
        "8",
        "Track lead in CRM (HubSpot)",
        1720,
        "POST",
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{client_email}","firstname":"{client_name}","phone":"{client_phone}","company":"{matter_type}","hs_lead_status":"OPEN","notes_last_contacted":"Fee estimate {estimate_id} sent. Practice area: {practice_area}. Range: {estimated_total_low}-{estimated_total_high}. Fee type: {fee_type}. Valid until: {estimate_valid_until}."}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // payment plan eligible -> generate options
      e("4", "6", "output_2"), // not eligible -> skip to document gen
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 780. MENTAL HEALTH CHECK-IN BOT (9 nodes)
  // ===================================================================

  "Mental Health Check-in Bot": {
    summary:
      "Scheduled mental health check-ins with validated assessments and crisis detection. A scheduled trigger initiates check-ins, a validated assessment is delivered, an AI conversational wrapper makes the assessment feel natural, score calculation and trend analysis run via runCode, a crisis detection condition routes emergencies, clinician SMS alerts fire for critical scores, EHR documentation is posted, patient coping resources are emailed, and a provider dashboard logs data to Sheets.",
    nodes: [
      trigger("Scheduled mental health check-in", "schedule", {
        schedule: "Every Monday and Thursday at 9:00 AM",
        howItStarts:
          "Automated schedule triggers patient check-ins on configured days for all active patients in the mental health program",
        setup:
          "Set the schedule to match your clinical check-in cadence. Configure patient list in the workflow settings. Ensure clinician contact info and EHR credentials are configured.",
        inputFields: [
          { name: "patient_name", type: "string", example: "Jane Doe" },
          {
            name: "patient_email",
            type: "string",
            example: "jane.doe@email.com",
          },
          { name: "patient_phone", type: "string", example: "+15551234567" },
          { name: "patient_id", type: "string", example: "PAT-001" },
          {
            name: "clinician_name",
            type: "string",
            example: "Dr. Sarah Mitchell",
          },
          { name: "clinician_phone", type: "string", example: "+15559876543" },
          {
            name: "clinician_email",
            type: "string",
            example: "s.mitchell@clinic.example.com",
          },
          { name: "assessment_type", type: "string", example: "PHQ-9" },
        ],
      }),

      // 2. Validated assessment delivery (sendMessage)
      n("2", "sendMessage", "Deliver validated assessment questions", 280, {
        sendMessageDetails: {
          message:
            "Hi {patient_name}, it is time for your regular mental health check-in.\\n\\nPlease rate each of the following over the past 2 weeks on a scale of 0-3:\\n(0 = Not at all, 1 = Several days, 2 = More than half the days, 3 = Nearly every day)\\n\\n1. Little interest or pleasure in doing things\\n2. Feeling down, depressed, or hopeless\\n3. Trouble falling/staying asleep, or sleeping too much\\n4. Feeling tired or having little energy\\n5. Poor appetite or overeating\\n6. Feeling bad about yourself\\n7. Trouble concentrating\\n8. Moving or speaking slowly / being fidgety\\n9. Thoughts of self-harm\\n\\nAlso share: How are you feeling overall today? Any concerns you want your care team to know about?",
        },
      }),

      // 3. AI conversational wrapper
      aiNode(
        "3",
        "AI: Process assessment responses conversationally",
        520,
        "You are a compassionate mental health assessment assistant. Parse the patient response to the PHQ-9 questionnaire. Extract individual scores for each of the 9 questions (q1_score through q9_score, each 0-3), the overall_mood description, and any specific_concerns mentioned. If the patient did not give clear numeric scores, infer them from their language. Respond JSON: {q1_score, q2_score, q3_score, q4_score, q5_score, q6_score, q7_score, q8_score, q9_score, overall_mood, specific_concerns, response_quality}",
        "Patient {patient_name} ({patient_id}) response to {assessment_type}: {webhook_payload}",
        [
          { field: "q1_score", as: "q1_score", type: "number" },
          { field: "q2_score", as: "q2_score", type: "number" },
          { field: "q3_score", as: "q3_score", type: "number" },
          { field: "q4_score", as: "q4_score", type: "number" },
          { field: "q5_score", as: "q5_score", type: "number" },
          { field: "q6_score", as: "q6_score", type: "number" },
          { field: "q7_score", as: "q7_score", type: "number" },
          { field: "q8_score", as: "q8_score", type: "number" },
          { field: "q9_score", as: "q9_score", type: "number" },
          { field: "overall_mood", as: "overall_mood", type: "string" },
          {
            field: "specific_concerns",
            as: "specific_concerns",
            type: "string",
          },
          { field: "response_quality", as: "response_quality", type: "string" },
        ],
      ),

      // 4. Score calculation + trend analysis (runCode)
      n("4", "runCode", "Calculate PHQ-9 score + trend analysis", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate PHQ-9 total score and severity classification
const scores = [
  parseInt(vars.q1_score) || 0, parseInt(vars.q2_score) || 0,
  parseInt(vars.q3_score) || 0, parseInt(vars.q4_score) || 0,
  parseInt(vars.q5_score) || 0, parseInt(vars.q6_score) || 0,
  parseInt(vars.q7_score) || 0, parseInt(vars.q8_score) || 0,
  parseInt(vars.q9_score) || 0,
];
const totalScore = scores.reduce((a, b) => a + b, 0);

// PHQ-9 severity classification
let severity = "minimal";
if (totalScore >= 20) severity = "severe";
else if (totalScore >= 15) severity = "moderately_severe";
else if (totalScore >= 10) severity = "moderate";
else if (totalScore >= 5) severity = "mild";

// Crisis detection: Q9 (self-harm) score of 2 or 3
const selfHarmScore = parseInt(vars.q9_score) || 0;
const isCrisis = selfHarmScore >= 2 || totalScore >= 20;

// Simulated trend (in production, compare with stored historical scores)
const previousScore = Math.max(0, totalScore + Math.floor(Math.random() * 6) - 3);
const trend = totalScore > previousScore ? "worsening" : totalScore < previousScore ? "improving" : "stable";
const scoreChange = totalScore - previousScore;

return {
  total_score: totalScore,
  severity: severity,
  is_crisis: isCrisis ? "yes" : "no",
  self_harm_flag: selfHarmScore >= 2 ? "yes" : "no",
  self_harm_score: selfHarmScore,
  trend: trend,
  score_change: scoreChange,
  previous_score: previousScore,
  assessment_date: new Date().toISOString().split("T")[0],
  clinical_action: isCrisis ? "IMMEDIATE_INTERVENTION" : severity === "moderately_severe" ? "URGENT_FOLLOW_UP" : severity === "moderate" ? "SCHEDULE_FOLLOW_UP" : "ROUTINE_MONITORING"
};`,
          outputVariables: [
            { name: "total_score", type: "number" },
            { name: "severity", type: "string" },
            { name: "is_crisis", type: "string" },
            { name: "self_harm_flag", type: "string" },
            { name: "self_harm_score", type: "number" },
            { name: "trend", type: "string" },
            { name: "score_change", type: "number" },
            { name: "previous_score", type: "number" },
            { name: "assessment_date", type: "string" },
            { name: "clinical_action", type: "string" },
          ],
        },
      }),

      // 5. Crisis detection condition
      n("5", "condition", "Is this a crisis situation?", 1000, {
        conditionData: [
          {
            variable: "is_crisis",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Clinician alert (SMS) — True path (crisis)
      smsNode(
        "6",
        "STAT: Alert clinician of crisis (SMS)",
        1240,
        "{clinician_phone}",
        "CRISIS ALERT - Patient {patient_name} ({patient_id}). PHQ-9 Score: {total_score}/27 ({severity}). Self-harm score: {self_harm_score}/3. Trend: {trend}. Clinical action: {clinical_action}. Please contact patient immediately at {patient_phone}. If unable to reach, initiate welfare check protocol.",
      ),

      // 7. EHR documentation
      emailNode(
        "7",
        "Document assessment in EHR",
        1480,
        "{clinician_email}",
        "PHQ-9 Assessment: {patient_name} ({patient_id}) - Score: {total_score}/27",
        "Clinical Documentation\\n\\nPatient: {patient_name} (ID: {patient_id})\\nAssessment: {assessment_type}\\nDate: {assessment_date}\\n\\nPHQ-9 Score: {total_score}/27\\nSeverity: {severity}\\nTrend: {trend} (change: {score_change} from previous {previous_score})\\n\\nItem Scores: Q1={q1_score} Q2={q2_score} Q3={q3_score} Q4={q4_score} Q5={q5_score} Q6={q6_score} Q7={q7_score} Q8={q8_score} Q9={q9_score}\\n\\nSelf-harm flag: {self_harm_flag} (Q9={self_harm_score})\\nOverall mood: {overall_mood}\\nSpecific concerns: {specific_concerns}\\n\\nRecommended action: {clinical_action}",
      ),

      // 8. Patient coping resources email
      emailNode(
        "8",
        "Send coping resources to patient",
        1720,
        "{patient_email}",
        "Your Check-In Summary + Resources",
        "Hi {patient_name},\\n\\nThank you for completing your mental health check-in today.\\n\\nYour overall mood: {overall_mood}\\nTrend since last check-in: {trend}\\n\\nHere are some resources that may help:\\n- Mindfulness exercise: Try 5 minutes of deep breathing today\\n- Crisis line: If you ever feel in crisis, call 988 (Suicide & Crisis Lifeline)\\n- Self-care reminder: Get adequate sleep, eat well, and stay connected with supportive people\\n\\nYour care team has been updated with your responses. {clinician_name} will follow up as needed.\\n\\nYour next check-in is scheduled automatically. Remember, reaching out is a sign of strength.",
      ),

      // 9. Provider dashboard (Sheets)
      sheetsNode(
        "9",
        "Log assessment data to provider dashboard (Sheets)",
        1960,
        "MentalHealthAssessments",
        '[["{patient_id}","{patient_name}","{assessment_date}","{assessment_type}","{total_score}","{severity}","{trend}","{score_change}","{self_harm_flag}","{is_crisis}","{clinical_action}","{overall_mood}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // crisis -> clinician SMS
      e("5", "7", "output_2"), // not crisis -> EHR documentation
      e("6", "7"), // after crisis alert, also document
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 781. MORTGAGE PRE-QUALIFICATION BOT (9 nodes)
  // ===================================================================

  "Mortgage Pre-Qualification Bot": {
    summary:
      "Guides borrowers through mortgage pre-qualification. An inquiry triggers an AI-guided application interview, DTI calculation via runCode determines affordability, a credit tier classification condition routes applicants, multi-program rate comparison finds the best options, a pre-qualification letter is generated via runCode, a down payment estimator calculates requirements, results are delivered by email, and CRM lead capture logs the prospect in HubSpot.",
    nodes: [
      trigger("Webhook: Mortgage pre-qualification inquiry", "webhook_app", {
        source: "Mortgage Website / Loan Officer Portal / Rate Widget",
        event: "mortgage.prequalification.inquiry",
        howItStarts:
          "A prospective borrower submits a pre-qualification request via the mortgage website, loan officer referral portal, or embedded rate widget",
        setup:
          "Configure your mortgage website or loan officer portal to POST pre-qualification requests to your workflow URL. Payload includes income, debt, and property details.",
        inputFields: [],
      }),

      // 2. AI guided application interview
      aiNode(
        "2",
        "AI: Extract borrower application details",
        280,
        "You are a mortgage loan specialist. Parse the pre-qualification inquiry and extract: borrower_name, borrower_email, borrower_phone, annual_income, monthly_debts (total monthly debt payments), employment_type (W2, self_employed, retired), employment_years, credit_score_range (excellent_740+, good_700-739, fair_660-699, below_660), property_type (single_family, condo, townhouse, multi_unit), purchase_price, down_payment_amount, loan_purpose (purchase, refinance), first_time_buyer (yes/no), veteran_status (yes/no), and property_state. Respond JSON.",
        "{webhook_payload}",
        [
          { field: "borrower_name", as: "borrower_name", type: "string" },
          { field: "borrower_email", as: "borrower_email", type: "string" },
          { field: "borrower_phone", as: "borrower_phone", type: "string" },
          { field: "annual_income", as: "annual_income", type: "number" },
          { field: "monthly_debts", as: "monthly_debts", type: "number" },
          { field: "employment_type", as: "employment_type", type: "string" },
          { field: "employment_years", as: "employment_years", type: "number" },
          {
            field: "credit_score_range",
            as: "credit_score_range",
            type: "string",
          },
          { field: "property_type", as: "property_type", type: "string" },
          { field: "purchase_price", as: "purchase_price", type: "number" },
          {
            field: "down_payment_amount",
            as: "down_payment_amount",
            type: "number",
          },
          { field: "loan_purpose", as: "loan_purpose", type: "string" },
          { field: "first_time_buyer", as: "first_time_buyer", type: "string" },
          { field: "veteran_status", as: "veteran_status", type: "string" },
          { field: "property_state", as: "property_state", type: "string" },
        ],
      ),

      // 3. DTI calculation (runCode)
      n("3", "runCode", "Calculate DTI + affordability metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate Debt-to-Income ratio and affordability
const annualIncome = parseFloat(vars.annual_income) || 0;
const monthlyIncome = annualIncome / 12;
const monthlyDebts = parseFloat(vars.monthly_debts) || 0;
const purchasePrice = parseFloat(vars.purchase_price) || 0;
const downPayment = parseFloat(vars.down_payment_amount) || 0;
const loanAmount = purchasePrice - downPayment;
const downPaymentPercent = purchasePrice > 0 ? Math.round((downPayment / purchasePrice) * 100) : 0;
const ltv = purchasePrice > 0 ? Math.round((loanAmount / purchasePrice) * 100) : 0;

// Estimated monthly payment (30yr at ~7%)
const rate = 0.07 / 12;
const n_payments = 360;
const monthlyPrincipalInterest = loanAmount > 0 ? Math.round(loanAmount * (rate * Math.pow(1 + rate, n_payments)) / (Math.pow(1 + rate, n_payments) - 1)) : 0;
const monthlyTaxInsurance = Math.round(purchasePrice * 0.015 / 12); // 1.5% annual
const pmi = ltv > 80 ? Math.round(loanAmount * 0.005 / 12) : 0;
const totalMonthlyHousing = monthlyPrincipalInterest + monthlyTaxInsurance + pmi;

// DTI calculations
const frontEndDTI = monthlyIncome > 0 ? Math.round((totalMonthlyHousing / monthlyIncome) * 100) : 0;
const backEndDTI = monthlyIncome > 0 ? Math.round(((totalMonthlyHousing + monthlyDebts) / monthlyIncome) * 100) : 0;

// Qualification determination
const creditRange = vars.credit_score_range || "";
const maxDTI = creditRange.includes("740") ? 50 : creditRange.includes("700") ? 45 : creditRange.includes("660") ? 43 : 36;
const qualifies = backEndDTI <= maxDTI ? "yes" : "no";
const creditTier = creditRange.includes("740") ? "excellent" : creditRange.includes("700") ? "good" : creditRange.includes("660") ? "fair" : "below_standard";

return {
  loan_amount: loanAmount,
  down_payment_percent: downPaymentPercent,
  ltv_ratio: ltv,
  monthly_pi: monthlyPrincipalInterest,
  monthly_tax_insurance: monthlyTaxInsurance,
  monthly_pmi: pmi,
  total_monthly_housing: totalMonthlyHousing,
  front_end_dti: frontEndDTI,
  back_end_dti: backEndDTI,
  max_allowed_dti: maxDTI,
  qualifies: qualifies,
  credit_tier: creditTier,
  monthly_income: Math.round(monthlyIncome)
};`,
          outputVariables: [
            { name: "loan_amount", type: "number" },
            { name: "down_payment_percent", type: "number" },
            { name: "ltv_ratio", type: "number" },
            { name: "monthly_pi", type: "number" },
            { name: "monthly_tax_insurance", type: "number" },
            { name: "monthly_pmi", type: "number" },
            { name: "total_monthly_housing", type: "number" },
            { name: "front_end_dti", type: "number" },
            { name: "back_end_dti", type: "number" },
            { name: "max_allowed_dti", type: "number" },
            { name: "qualifies", type: "string" },
            { name: "credit_tier", type: "string" },
            { name: "monthly_income", type: "number" },
          ],
        },
      }),

      // 4. Credit tier classification condition
      n("4", "condition", "Does borrower pre-qualify?", 760, {
        conditionData: [
          {
            variable: "qualifies",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Multi-program rate comparison (True — qualifies)
      aiNode(
        "5",
        "AI: Compare loan programs + rates",
        1000,
        "You are a mortgage rate specialist. Based on the borrower profile, compare available loan programs: Conventional (if credit tier is good+), FHA (always available, 3.5% min down), VA (if veteran), USDA (if rural property). For each eligible program, provide the estimated rate, monthly payment, total cost over 30 years, and pros/cons. Recommend the best program. Respond JSON: {programs_compared, recommended_program, recommended_rate, recommended_monthly, program_comparison_summary, special_programs_eligible}",
        "Credit tier: {credit_tier}. Score range: {credit_score_range}. Loan amount: ${loan_amount}. LTV: {ltv_ratio}%. DTI: {back_end_dti}%. Down payment: {down_payment_percent}%. First time: {first_time_buyer}. Veteran: {veteran_status}. Property: {property_type}. State: {property_state}. Employment: {employment_type} ({employment_years} yrs).",
        [
          {
            field: "programs_compared",
            as: "programs_compared",
            type: "string",
          },
          {
            field: "recommended_program",
            as: "recommended_program",
            type: "string",
          },
          { field: "recommended_rate", as: "recommended_rate", type: "string" },
          {
            field: "recommended_monthly",
            as: "recommended_monthly",
            type: "string",
          },
          {
            field: "program_comparison_summary",
            as: "program_comparison",
            type: "string",
          },
          {
            field: "special_programs_eligible",
            as: "special_programs",
            type: "string",
          },
        ],
      ),

      // 6. Pre-qualification letter generation (runCode)
      n("6", "runCode", "Generate pre-qualification letter", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate pre-qualification letter reference
const prequalId = "PQ-" + Date.now().toString(36).toUpperCase();
const validDays = 90;
const validUntil = new Date();
validUntil.setDate(validUntil.getDate() + validDays);
const maxPurchase = Math.round(parseFloat(vars.loan_amount || 0) / 0.8); // 80% LTV max
return {
  prequal_id: prequalId,
  prequal_valid_until: validUntil.toISOString().split("T")[0],
  prequal_letter_url: "https://mortgage.example.com/letters/" + prequalId,
  max_purchase_price: maxPurchase,
  max_purchase_display: "$" + maxPurchase.toLocaleString()
};`,
          outputVariables: [
            { name: "prequal_id", type: "string" },
            { name: "prequal_valid_until", type: "string" },
            { name: "prequal_letter_url", type: "string" },
            { name: "max_purchase_price", type: "number" },
            { name: "max_purchase_display", type: "string" },
          ],
        },
      }),

      // 7. Down payment estimator (sendMessage)
      n(
        "7",
        "sendMessage",
        "Present down payment + closing cost estimate",
        1480,
        {
          sendMessageDetails: {
            message:
              "Pre-Qualification Summary for {borrower_name}:\\n\\nPre-Qual ID: {prequal_id}\\nMax purchase: {max_purchase_display}\\nRecommended program: {recommended_program} at {recommended_rate}\\n\\nDown payment: ${down_payment_amount} ({down_payment_percent}%)\\nEstimated closing costs: 2-5% of loan amount\\nCash needed at closing: approximately ${down_payment_amount} + closing costs\\n\\nMonthly payment estimate:\\n- Principal + Interest: ${monthly_pi}\\n- Taxes + Insurance: ${monthly_tax_insurance}\\n- PMI: ${monthly_pmi}\\n- Total: ${total_monthly_housing}\\n\\nDTI: {back_end_dti}% (max allowed: {max_allowed_dti}%)",
          },
        },
      ),

      // 8. Results delivery email
      emailNode(
        "8",
        "Send pre-qualification results email",
        1720,
        "{borrower_email}",
        "You Are Pre-Qualified! - {prequal_id}",
        "Dear {borrower_name},\\n\\nCongratulations! Based on the information you provided, you pre-qualify for a mortgage.\\n\\nPre-Qualification ID: {prequal_id}\\nValid Until: {prequal_valid_until}\\nMax Purchase Price: {max_purchase_display}\\n\\nRecommended Program: {recommended_program}\\nEstimated Rate: {recommended_rate}\\nEstimated Monthly Payment: {recommended_monthly}\\n\\n{program_comparison}\\n\\nSpecial programs you may qualify for: {special_programs}\\n\\nNext Steps:\\n1. Download your pre-qualification letter: {prequal_letter_url}\\n2. Get fully pre-approved (requires documentation)\\n3. Start house shopping with confidence!\\n\\nThis is a pre-qualification based on self-reported information. Full approval requires documentation verification.",
      ),

      // 9. CRM lead capture (HubSpot)
      hubspotNode(
        "9",
        "Capture mortgage lead (HubSpot)",
        1960,
        "POST",
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{borrower_email}","firstname":"{borrower_name}","phone":"{borrower_phone}","hs_lead_status":"OPEN","notes_last_contacted":"Mortgage pre-qual {prequal_id}. Program: {recommended_program}. Rate: {recommended_rate}. Max purchase: {max_purchase_display}. DTI: {back_end_dti}%. Credit: {credit_tier}. Qualifies: {qualifies}."}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // qualifies -> program comparison
      e("4", "8", "output_2"), // does not qualify -> send denial/improvement email
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 782. NOTARY APPOINTMENT SCHEDULER BOT (7 nodes)
  // ===================================================================

  "Notary Appointment Scheduler Bot": {
    summary:
      "Schedules notary appointments with document requirements assessment. A scheduling request triggers document requirements assessment via runCode, notary availability is searched, an in-person vs remote condition routes the booking, a confirmation email is sent, a delay triggers a pre-appointment reminder, and post-appointment documentation with payment is processed.",
    nodes: [
      trigger("Webhook: Notary appointment request", "webhook_app", {
        source: "Notary Website / Legal Portal / Mobile App",
        event: "notary.appointment.request",
        howItStarts:
          "A client requests a notary appointment via the notary website, legal services portal, or mobile app",
        setup:
          "Configure your notary booking page or legal portal to POST appointment requests to your workflow URL. Payload includes document type and client details.",
        inputFields: [],
      }),

      // 2. Document requirements assessment (runCode)
      n("2", "runCode", "Assess document requirements + extract details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse notary appointment request and assess document requirements
const payload = JSON.parse(vars.webhook_payload || "{}");
const clientName = payload.name || payload.client_name || (payload.first_name || "") + " " + (payload.last_name || "");
const clientEmail = payload.email || payload.client_email || "";
const clientPhone = payload.phone || payload.client_phone || "";
const docType = (payload.document_type || payload.doc_type || "general").toLowerCase();
const signerCount = parseInt(payload.signer_count || payload.signers || 1);
const preferredDate = payload.preferred_date || payload.date || "";
const preferredTime = payload.preferred_time || payload.time || "";
const locationType = payload.location_type || payload.type || "in_person";

// Document-specific requirements
const requirements = {
  real_estate: { fee: 15000, witnessNeeded: true, idRequired: "government_photo_id", prepTime: 30, docs: "Deed, mortgage documents, closing paperwork" },
  power_of_attorney: { fee: 10000, witnessNeeded: true, idRequired: "government_photo_id", prepTime: 20, docs: "POA document, principal and agent IDs" },
  will: { fee: 10000, witnessNeeded: true, idRequired: "government_photo_id", prepTime: 25, docs: "Will document, two witness IDs" },
  affidavit: { fee: 5000, witnessNeeded: false, idRequired: "government_photo_id", prepTime: 15, docs: "Affidavit document" },
  general: { fee: 5000, witnessNeeded: false, idRequired: "government_photo_id", prepTime: 15, docs: "Document to be notarized" },
  loan_signing: { fee: 20000, witnessNeeded: false, idRequired: "government_photo_id_x2", prepTime: 45, docs: "Full loan package from title company" },
};

const req = requirements[docType] || requirements.general;
const totalFee = req.fee * signerCount;
const isRemoteEligible = docType !== "will" && docType !== "real_estate";
const isRemote = locationType === "remote" && isRemoteEligible ? "yes" : "no";

return {
  client_name: clientName.trim(),
  client_email: clientEmail,
  client_phone: clientPhone,
  document_type: docType,
  signer_count: signerCount,
  preferred_date: preferredDate,
  preferred_time: preferredTime,
  is_remote: isRemote,
  fee_cents: totalFee,
  fee_display: "$" + (totalFee / 100).toFixed(2),
  witness_needed: req.witnessNeeded ? "yes" : "no",
  id_required: req.idRequired,
  prep_time_minutes: req.prepTime,
  documents_to_bring: req.docs,
  appointment_id: "NOT-" + Date.now().toString(36).toUpperCase()
};`,
          outputVariables: [
            { name: "client_name", type: "string" },
            { name: "client_email", type: "string" },
            { name: "client_phone", type: "string" },
            { name: "document_type", type: "string" },
            { name: "signer_count", type: "number" },
            { name: "preferred_date", type: "string" },
            { name: "preferred_time", type: "string" },
            { name: "is_remote", type: "string" },
            { name: "fee_cents", type: "number" },
            { name: "fee_display", type: "string" },
            { name: "witness_needed", type: "string" },
            { name: "id_required", type: "string" },
            { name: "prep_time_minutes", type: "number" },
            { name: "documents_to_bring", type: "string" },
            { name: "appointment_id", type: "string" },
          ],
        },
      }),

      // 3. Notary availability search (AI)
      aiNode(
        "3",
        "AI: Find available notary + time slot",
        520,
        "You are a notary scheduling assistant. Based on the client preferred date/time and document type, find an available notary. Consider: notary certifications for the document type, geographic proximity for in-person appointments, RON (Remote Online Notarization) capability for remote appointments. Respond JSON: {notary_name, notary_email, notary_phone, confirmed_date, confirmed_time, notary_location, meeting_link_if_remote, notary_available}",
        "Document: {document_type}. Signers: {signer_count}. Preferred: {preferred_date} at {preferred_time}. Remote: {is_remote}. Witness needed: {witness_needed}. Prep time: {prep_time_minutes} min.",
        [
          { field: "notary_name", as: "notary_name", type: "string" },
          { field: "notary_email", as: "notary_email", type: "string" },
          { field: "notary_phone", as: "notary_phone", type: "string" },
          { field: "confirmed_date", as: "confirmed_date", type: "string" },
          { field: "confirmed_time", as: "confirmed_time", type: "string" },
          { field: "notary_location", as: "notary_location", type: "string" },
          {
            field: "meeting_link_if_remote",
            as: "meeting_link",
            type: "string",
          },
          { field: "notary_available", as: "notary_available", type: "string" },
        ],
      ),

      // 4. In-person vs remote condition
      n("4", "condition", "Is this a remote notarization?", 760, {
        conditionData: [
          {
            variable: "is_remote",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Confirmation email (handles both paths)
      emailNode(
        "5",
        "Send appointment confirmation email",
        1000,
        "{client_email}",
        "Notary Appointment Confirmed - {appointment_id}",
        "Dear {client_name},\\n\\nYour notary appointment has been confirmed.\\n\\nAppointment ID: {appointment_id}\\nDocument Type: {document_type}\\nDate: {confirmed_date}\\nTime: {confirmed_time}\\nNotary: {notary_name}\\nType: {is_remote}\\nLocation: {notary_location}\\nMeeting Link (if remote): {meeting_link}\\n\\nFee: {fee_display} (per {signer_count} signer(s))\\nEstimated Duration: {prep_time_minutes} minutes\\n\\nWhat to Bring:\\n- {id_required}\\n- {documents_to_bring}\\n- Witness required: {witness_needed}\\n\\nPlease arrive 10 minutes early. Cancellations require 24 hours notice.",
      ),

      // 6. Pre-appointment reminder (delay)
      n("6", "delayAction", "Wait until day before appointment", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 24, unit: "hours" },
        },
      }),

      // 7. Post-appointment documentation + payment
      emailNode(
        "7",
        "Send reminder + post-appointment payment info",
        1480,
        "{client_email}",
        "Reminder: Your notary appointment is tomorrow - {appointment_id}",
        "Dear {client_name},\\n\\nThis is a reminder for your notary appointment tomorrow.\\n\\nDate: {confirmed_date}\\nTime: {confirmed_time}\\nNotary: {notary_name}\\nLocation: {notary_location}\\n\\nChecklist:\\n- Bring valid {id_required}\\n- Bring: {documents_to_bring}\\n- Witness arranged: {witness_needed}\\n- Payment ready: {fee_display}\\n\\nPayment is due at the time of service. We accept cash, check, and credit card.\\n\\nAfter your appointment, you will receive a digital copy of the notarized document(s) within 24 hours.\\n\\nNeed to reschedule? Reply to this email or call {notary_phone}.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // remote -> confirmation with meeting link
      e("4", "5", "output_2"), // in-person -> confirmation with location
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 783. OPEN HOUSE FOLLOW-UP SEQUENCE (8 nodes)
  // ===================================================================

  "Open House Follow-Up Sequence": {
    summary:
      "Automated open house lead follow-up from sign-in to engagement-based escalation. An open house sign-in triggers visitor segmentation via runCode, a CRM record is created in HubSpot, an immediate thank-you email is sent, hot leads get a Slack alert to the listing agent, a 3-day delay fires a CMA email, a 7-day delay sends property matches, and an engagement-based escalation condition routes highly engaged leads to priority follow-up.",
    nodes: [
      trigger("Webhook: Open house sign-in", "webhook_app", {
        source: "Open House Sign-In App / Tablet Kiosk / QR Code",
        event: "openhouse.visitor.signin",
        howItStarts:
          "A visitor signs in at an open house via a tablet sign-in app, QR code, or paper-to-digital sign-in sheet",
        setup:
          "Configure your open house sign-in app (Spacio, Curb Hero, etc.) to POST visitor data to your workflow URL. Include all fields collected during sign-in.",
        inputFields: [],
      }),

      // 2. Visitor segmentation (runCode)
      n("2", "runCode", "Segment visitor + calculate lead score", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse open house sign-in data and segment visitor
const payload = JSON.parse(vars.webhook_payload || "{}");
const name = payload.name || payload.visitor_name || (payload.first_name || "") + " " + (payload.last_name || "");
const email = payload.email || payload.visitor_email || "";
const phone = payload.phone || payload.visitor_phone || "";
const propertyAddress = payload.property_address || payload.listing_address || "";
const listingPrice = parseFloat(payload.listing_price || payload.price || 0);
const agentName = payload.listing_agent || payload.agent_name || "";
const agentEmail = payload.agent_email || "";

// Scoring factors
let score = 0;
const preApproved = (payload.pre_approved || payload.preapproved || "").toLowerCase() === "yes";
if (preApproved) score += 30;
const hasAgent = (payload.has_agent || payload.working_with_agent || "").toLowerCase() === "yes";
if (!hasAgent) score += 20; // unrepresented buyer = opportunity
const timeline = (payload.timeline || payload.buying_timeline || "").toLowerCase();
if (timeline.includes("immediate") || timeline.includes("0-3")) score += 25;
else if (timeline.includes("3-6") || timeline.includes("soon")) score += 15;
else if (timeline.includes("6-12")) score += 5;
const priceRange = payload.price_range || payload.budget || "";
if (listingPrice > 0 && priceRange) score += 10;
if (phone) score += 5;
if (email) score += 5;

const segment = score >= 50 ? "hot" : score >= 25 ? "warm" : "cold";
const isHotLead = segment === "hot" ? "yes" : "no";

return {
  visitor_name: name.trim(),
  visitor_email: email,
  visitor_phone: phone,
  property_address: propertyAddress,
  listing_price: listingPrice,
  agent_name: agentName,
  agent_email: agentEmail,
  pre_approved: preApproved ? "yes" : "no",
  has_own_agent: hasAgent ? "yes" : "no",
  buying_timeline: timeline || "not specified",
  price_range: priceRange || "not specified",
  lead_score: score,
  lead_segment: segment,
  is_hot_lead: isHotLead,
  visit_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "visitor_name", type: "string" },
            { name: "visitor_email", type: "string" },
            { name: "visitor_phone", type: "string" },
            { name: "property_address", type: "string" },
            { name: "listing_price", type: "number" },
            { name: "agent_name", type: "string" },
            { name: "agent_email", type: "string" },
            { name: "pre_approved", type: "string" },
            { name: "has_own_agent", type: "string" },
            { name: "buying_timeline", type: "string" },
            { name: "price_range", type: "string" },
            { name: "lead_score", type: "number" },
            { name: "lead_segment", type: "string" },
            { name: "is_hot_lead", type: "string" },
            { name: "visit_date", type: "string" },
          ],
        },
      }),

      // 3. CRM record creation (HubSpot)
      hubspotNode(
        "3",
        "Create CRM contact (HubSpot)",
        520,
        "POST",
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{visitor_email}","firstname":"{visitor_name}","phone":"{visitor_phone}","hs_lead_status":"NEW","notes_last_contacted":"Open house visitor at {property_address} on {visit_date}. Segment: {lead_segment}. Score: {lead_score}. Pre-approved: {pre_approved}. Timeline: {buying_timeline}. Has agent: {has_own_agent}."}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),

      // 4. Immediate thank-you email
      emailNode(
        "4",
        "Send immediate thank-you email",
        760,
        "{visitor_email}",
        "Thank you for visiting {property_address}!",
        "Hi {visitor_name},\\n\\nThank you for visiting the open house at {property_address} today!\\n\\nI hope you enjoyed touring the property. Listed at ${listing_price}, this home has a lot to offer.\\n\\nHere are some highlights you may have noticed:\\n- Location and neighborhood details\\n- Property features and recent updates\\n- Schools, shopping, and commute info nearby\\n\\nI would love to answer any questions you have about this property or others in the area. Feel free to reply to this email or call me directly.\\n\\nBest regards,\\n{agent_name}",
      ),

      // 5. Hot lead Slack alert
      n("5", "condition", "Is this a hot lead?", 1000, {
        conditionData: [
          {
            variable: "is_hot_lead",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Slack alert for hot leads (True)
      slackNode(
        "6",
        "Alert listing agent of hot lead (Slack)",
        1240,
        "HOT LEAD from Open House at {property_address}\\n\\nVisitor: {visitor_name}\\nEmail: {visitor_email}\\nPhone: {visitor_phone}\\nLead Score: {lead_score}/100 (segment: {lead_segment})\\n\\nPre-approved: {pre_approved}\\nHas own agent: {has_own_agent}\\nTimeline: {buying_timeline}\\nBudget: {price_range}\\n\\nFollow up within 1 hour for best conversion!",
        400,
      ),

      // 7. Day-3 CMA email (delay)
      n("7", "delayAction", "Wait 3 days for follow-up", 1480, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 72, unit: "hours" },
        },
      }),

      // 8. Day-3 CMA + Day-7 property matches email
      emailNode(
        "8",
        "Send CMA + property matches email",
        1720,
        "{visitor_email}",
        "Market Update for {property_address} + Similar Properties",
        "Hi {visitor_name},\\n\\nI wanted to follow up on your visit to {property_address}.\\n\\nHere is a quick market update for the area:\\n- Average price per sq ft in this neighborhood\\n- Recent comparable sales\\n- Days on market trends\\n- Price trend direction\\n\\nBased on your interest, here are similar properties you might like:\\n1. [Similar property in same price range]\\n2. [Similar property in same neighborhood]\\n3. [Similar property with matching features]\\n\\nWould you like to schedule a private showing for any of these? I am available this week and would be happy to help.\\n\\nTimeline reminder: You mentioned your buying timeline is {buying_timeline}. Let us make sure you find the right home!\\n\\nBest regards,\\n{agent_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // hot lead -> Slack alert
      e("5", "7", "output_2"), // warm/cold -> skip to delayed follow-up
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 784. PARENT-TEACHER CONFERENCE SCHEDULER (8 nodes)
  // ===================================================================

  "Parent-Teacher Conference Scheduler": {
    summary:
      "Manages parent-teacher conference scheduling from launch to no-show handling. A conference launch trigger collects teacher availability, a parent invitation email is sent, scheduling conflict resolution runs via runCode, a sibling conflict condition ensures parents with multiple children are not double-booked, confirmation with calendar sync is sent, a day-before reminder email goes out, and no-show management handles missed conferences.",
    nodes: [
      trigger("Conference scheduling launch", "manual", {
        howItStarts:
          "School administrator launches the parent-teacher conference scheduling process for the upcoming conference period",
        setup:
          "Configure the conference date range, available time slots, and teacher list. Ensure parent contact information is loaded from the student information system.",
        inputFields: [
          { name: "conference_date", type: "string", example: "2026-04-15" },
          {
            name: "school_name",
            type: "string",
            example: "Lincoln Elementary",
          },
          { name: "parent_name", type: "string", example: "John Smith" },
          {
            name: "parent_email",
            type: "string",
            example: "john.smith@email.com",
          },
          { name: "student_name", type: "string", example: "Emily Smith" },
          { name: "teacher_name", type: "string", example: "Mrs. Johnson" },
          {
            name: "teacher_email",
            type: "string",
            example: "johnson@school.example.com",
          },
          { name: "grade_level", type: "string", example: "3rd Grade" },
          { name: "has_siblings", type: "string", example: "yes" },
          {
            name: "sibling_teachers",
            type: "string",
            example: "Mr. Davis, Ms. Lee",
          },
        ],
      }),

      // 2. Teacher availability collection (AI)
      aiNode(
        "2",
        "AI: Generate available time slots",
        280,
        "You are a school scheduling assistant. Based on the conference date and teacher schedule, generate a list of available 15-minute time slots between 3:00 PM and 7:00 PM. Reserve a 15-minute break every 2 hours. Also consider whether this parent has siblings in other classes and flag if sibling scheduling coordination is needed. Respond JSON: {available_slots, total_slots, slot_duration_minutes, break_times, needs_sibling_coordination, coordination_teachers, recommended_slot}",
        "Conference date: {conference_date}. School: {school_name}. Teacher: {teacher_name}. Parent: {parent_name}. Student: {student_name}. Grade: {grade_level}. Has siblings: {has_siblings}. Sibling teachers: {sibling_teachers}.",
        [
          { field: "available_slots", as: "available_slots", type: "string" },
          { field: "total_slots", as: "total_slots", type: "number" },
          {
            field: "slot_duration_minutes",
            as: "slot_duration",
            type: "number",
          },
          {
            field: "needs_sibling_coordination",
            as: "needs_sibling_coord",
            type: "string",
          },
          {
            field: "coordination_teachers",
            as: "coordination_teachers",
            type: "string",
          },
          { field: "recommended_slot", as: "recommended_slot", type: "string" },
        ],
      ),

      // 3. Parent invitation email
      emailNode(
        "3",
        "Send conference invitation to parent",
        520,
        "{parent_email}",
        "Schedule Your Conference: {student_name} - {teacher_name} ({conference_date})",
        "Dear {parent_name},\\n\\nParent-teacher conferences at {school_name} are coming up!\\n\\nStudent: {student_name}\\nTeacher: {teacher_name}\\nGrade: {grade_level}\\nDate: {conference_date}\\n\\nAvailable time slots (15 minutes each):\\n{available_slots}\\n\\nRecommended slot: {recommended_slot}\\n\\nPlease reply with your preferred time, or click the scheduling link below to book online.\\n\\nSchedule now: https://school.example.com/conference/book\\n\\nIf you have children in multiple classes, we will coordinate your appointments back-to-back.",
      ),

      // 4. Scheduling conflict resolution (runCode)
      n("4", "runCode", "Resolve scheduling conflicts", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Resolve scheduling conflicts for sibling coordination
const hasSiblings = vars.has_siblings === "yes";
const siblingTeachers = (vars.sibling_teachers || "").split(",").map(t => t.trim()).filter(Boolean);
const recommendedSlot = vars.recommended_slot || "3:30 PM";
const needsCoord = vars.needs_sibling_coord === "yes";

// Parse recommended time to calculate sequential slots
const timeParts = recommendedSlot.match(/(\\d+):(\\d+)\\s*(AM|PM)/i) || [];
let hour = parseInt(timeParts[1]) || 3;
const minutes = parseInt(timeParts[2]) || 30;
const isPM = (timeParts[3] || "PM").toUpperCase() === "PM";
if (isPM && hour < 12) hour += 12;

const slots = [{ teacher: vars.teacher_name, time: recommendedSlot }];
if (hasSiblings && siblingTeachers.length > 0) {
  siblingTeachers.forEach((t, i) => {
    const nextMinutes = minutes + (15 * (i + 1));
    const nextHour = hour + Math.floor(nextMinutes / 60);
    const nextMin = nextMinutes % 60;
    const displayHour = nextHour > 12 ? nextHour - 12 : nextHour;
    const ampm = nextHour >= 12 ? "PM" : "AM";
    slots.push({ teacher: t, time: displayHour + ":" + String(nextMin).padStart(2, "0") + " " + ampm });
  });
}

const hasSiblingConflict = hasSiblings && siblingTeachers.length > 2 ? "yes" : "no";

return {
  final_schedule: slots.map(s => s.teacher + " at " + s.time).join("; "),
  conference_count: slots.length,
  has_sibling_conflict: hasSiblingConflict,
  total_time_needed: (slots.length * 15) + " minutes",
  first_appointment: slots[0].time,
  last_appointment: slots[slots.length - 1].time
};`,
          outputVariables: [
            { name: "final_schedule", type: "string" },
            { name: "conference_count", type: "number" },
            { name: "has_sibling_conflict", type: "string" },
            { name: "total_time_needed", type: "string" },
            { name: "first_appointment", type: "string" },
            { name: "last_appointment", type: "string" },
          ],
        },
      }),

      // 5. Sibling conflict condition
      n("5", "condition", "Any sibling scheduling conflicts?", 1000, {
        conditionData: [
          {
            variable: "has_sibling_conflict",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Confirmation + calendar sync
      emailNode(
        "6",
        "Send confirmation + calendar invite",
        1240,
        "{parent_email}",
        "Conference Confirmed: {student_name} - {conference_date}",
        "Dear {parent_name},\\n\\nYour parent-teacher conference(s) are confirmed!\\n\\nDate: {conference_date}\\n\\nSchedule:\\n{final_schedule}\\n\\nTotal time needed: {total_time_needed}\\nFirst appointment: {first_appointment}\\nLast appointment: {last_appointment}\\n\\nLocation: {school_name}\\n\\nA calendar invitation has been sent. Please arrive 5 minutes before your first appointment.\\n\\nTopics to discuss:\\n- Academic progress\\n- Behavior and social development\\n- Areas for improvement\\n- How to support learning at home\\n\\nIf you need to reschedule, please do so at least 48 hours in advance.",
      ),

      // 7. Day-before reminder email
      n("7", "delayAction", "Wait until day before conference", 1480, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 24, unit: "hours" },
        },
      }),

      // 8. Reminder + no-show management
      emailNode(
        "8",
        "Send day-before reminder email",
        1720,
        "{parent_email}",
        "Reminder: Conferences Tomorrow at {school_name}",
        "Dear {parent_name},\\n\\nThis is a friendly reminder about your parent-teacher conference(s) tomorrow.\\n\\nDate: {conference_date}\\nSchedule: {final_schedule}\\nTotal conferences: {conference_count}\\nTotal time: {total_time_needed}\\n\\nParking is available in the main lot. Enter through the front office.\\n\\nIf you cannot attend, please:\\n1. Reply to this email immediately\\n2. Call the school office to reschedule\\n3. Request a phone/video alternative if needed\\n\\nNo-shows without notice may not be rescheduled until the next conference period.\\n\\nWe look forward to partnering with you in {student_name}'s education!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // sibling conflict -> still send confirmation (manual resolution)
      e("5", "6", "output_2"), // no conflict -> confirmation
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 785. PATENT APPLICATION TRACKER (8 nodes)
  // ===================================================================

  "Patent Application Tracker": {
    summary:
      "Tracks patent application status across USPTO and foreign filings. A weekly schedule triggers USPTO status queries, foreign filing status is checked, deadline proximity classification runs via runCode, a critical deadline condition routes urgent items, AI summarizes office actions, a portfolio dashboard is updated in Sheets, and a quarterly IP portfolio report is emailed.",
    nodes: [
      trigger("Weekly patent status check", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Automated weekly schedule checks all active patent applications for status changes, deadlines, and required actions",
        setup:
          "Configure your patent portfolio list with application numbers, filing dates, and assigned attorneys. Ensure USPTO API credentials are set up.",
        inputFields: [
          { name: "portfolio_id", type: "string", example: "IP-PORT-001" },
          {
            name: "company_name",
            type: "string",
            example: "Acme Innovations Inc.",
          },
          {
            name: "ip_counsel_email",
            type: "string",
            example: "ip.counsel@company.example.com",
          },
          {
            name: "patent_applications_json",
            type: "string",
            example:
              '[{"app_number":"17/123456","title":"Widget System","filing_date":"2025-01-15","attorney":"J. Smith"}]',
          },
        ],
      }),

      // 2. USPTO status query
      n("2", "hitApi", "Query USPTO patent status (PAIR API)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://developer.uspto.gov/ibd-api/v1/application/grants?applicationNumber={app_number}",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "results",
              customName: "uspto_results_json",
              type: "string",
            },
            { name: "count", customName: "uspto_result_count", type: "number" },
          ],
        },
      }),

      // 3. Foreign filing status check
      aiNode(
        "3",
        "AI: Analyze USPTO + foreign filing status",
        520,
        "You are a patent portfolio analyst. Parse the USPTO query results and the patent application list. For each application, determine: current_status (pending, published, allowed, granted, abandoned, office_action_pending), last_action_date, next_deadline, foreign_filing_status (if applicable — PCT, EP, JP, CN, KR filings), and any pending office actions. Summarize the overall portfolio health. Respond JSON: {portfolio_summary, applications_with_deadlines, office_actions_pending, total_active, total_granted, total_abandoned, foreign_filings_status, next_critical_deadline, days_to_critical}",
        "Portfolio: {portfolio_id}. Company: {company_name}. USPTO results: {uspto_results_json}. Applications: {patent_applications_json}.",
        [
          {
            field: "portfolio_summary",
            as: "portfolio_summary",
            type: "string",
          },
          {
            field: "applications_with_deadlines",
            as: "apps_with_deadlines",
            type: "string",
          },
          {
            field: "office_actions_pending",
            as: "office_actions_pending",
            type: "string",
          },
          { field: "total_active", as: "total_active", type: "number" },
          { field: "total_granted", as: "total_granted", type: "number" },
          { field: "total_abandoned", as: "total_abandoned", type: "number" },
          {
            field: "foreign_filings_status",
            as: "foreign_filings",
            type: "string",
          },
          {
            field: "next_critical_deadline",
            as: "next_critical_deadline",
            type: "string",
          },
          { field: "days_to_critical", as: "days_to_critical", type: "number" },
        ],
      ),

      // 4. Deadline proximity classification (runCode)
      n("4", "runCode", "Classify deadline proximity + urgency", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify patent deadline proximity and determine alerts needed
const daysToCritical = parseInt(vars.days_to_critical) || 999;
const officeActions = vars.office_actions_pending || "none";
const hasOfficeActions = officeActions.toLowerCase() !== "none" && officeActions.length > 5;

let urgency = "routine";
let isCritical = "no";
if (daysToCritical <= 14) { urgency = "critical"; isCritical = "yes"; }
else if (daysToCritical <= 30) { urgency = "urgent"; isCritical = "yes"; }
else if (daysToCritical <= 60) { urgency = "attention_needed"; }
else if (hasOfficeActions) { urgency = "action_required"; }

const weekNumber = Math.ceil((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 604800000);
const isQuarterEnd = [13, 26, 39, 52].includes(weekNumber);

return {
  deadline_urgency: urgency,
  is_critical_deadline: isCritical,
  has_office_actions: hasOfficeActions ? "yes" : "no",
  days_to_deadline: daysToCritical,
  check_date: new Date().toISOString().split("T")[0],
  is_quarter_end: isQuarterEnd ? "yes" : "no",
  alert_level: isCritical === "yes" ? "HIGH" : hasOfficeActions ? "MEDIUM" : "LOW"
};`,
          outputVariables: [
            { name: "deadline_urgency", type: "string" },
            { name: "is_critical_deadline", type: "string" },
            { name: "has_office_actions", type: "string" },
            { name: "days_to_deadline", type: "number" },
            { name: "check_date", type: "string" },
            { name: "is_quarter_end", type: "string" },
            { name: "alert_level", type: "string" },
          ],
        },
      }),

      // 5. Critical deadline condition
      n("5", "condition", "Is there a critical deadline approaching?", 1000, {
        conditionData: [
          {
            variable: "is_critical_deadline",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI office action summary (True — critical)
      aiNode(
        "6",
        "AI: Summarize office actions + recommend responses",
        1240,
        "You are a patent prosecution specialist. Analyze the pending office actions and critical deadlines. For each, provide a plain-language summary of what the USPTO or foreign office is requesting, the deadline, recommended response strategy, estimated cost, and priority level. Respond JSON: {action_summaries, response_strategies, total_estimated_cost, priority_actions, deadline_calendar}",
        "Portfolio: {portfolio_id}. Office actions: {office_actions_pending}. Deadlines: {apps_with_deadlines}. Next critical: {next_critical_deadline} ({days_to_deadline} days). Foreign: {foreign_filings}.",
        [
          { field: "action_summaries", as: "action_summaries", type: "string" },
          {
            field: "response_strategies",
            as: "response_strategies",
            type: "string",
          },
          {
            field: "total_estimated_cost",
            as: "estimated_cost",
            type: "string",
          },
          { field: "priority_actions", as: "priority_actions", type: "string" },
          {
            field: "deadline_calendar",
            as: "deadline_calendar",
            type: "string",
          },
        ],
      ),

      // 7. Portfolio dashboard (Sheets)
      sheetsNode(
        "7",
        "Update patent portfolio dashboard (Sheets)",
        1480,
        "PatentPortfolio",
        '[["{check_date}","{portfolio_id}","{company_name}","{total_active}","{total_granted}","{total_abandoned}","{alert_level}","{deadline_urgency}","{next_critical_deadline}","{days_to_deadline}","{has_office_actions}"]]',
      ),

      // 8. Quarterly IP portfolio report (email)
      emailNode(
        "8",
        "Send IP portfolio status report",
        1720,
        "{ip_counsel_email}",
        "Patent Portfolio Report - {company_name} ({check_date})",
        "Patent Portfolio Status Report\\n\\nCompany: {company_name}\\nPortfolio ID: {portfolio_id}\\nReport Date: {check_date}\\n\\nPortfolio Summary:\\n{portfolio_summary}\\n\\nActive Applications: {total_active}\\nGranted Patents: {total_granted}\\nAbandoned: {total_abandoned}\\n\\nAlert Level: {alert_level}\\nNext Critical Deadline: {next_critical_deadline} ({days_to_deadline} days)\\n\\nPending Office Actions:\\n{office_actions_pending}\\n\\nForeign Filings:\\n{foreign_filings}\\n\\nApplications with Upcoming Deadlines:\\n{apps_with_deadlines}\\n\\nPlease review and take action on any critical items.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // critical -> office action analysis
      e("5", "7", "output_2"), // not critical -> log to dashboard
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 786. PATIENT APPOINTMENT SCHEDULER (9 nodes)
  // ===================================================================

  "Patient Appointment Scheduler": {
    summary:
      "Full patient appointment scheduling with insurance pre-check and smart reminders. An appointment request triggers insurance eligibility pre-check, a coverage issue condition routes uninsured patients, provider availability search finds slots, AI recommends optimal appointment times, the appointment is booked, a confirmation email is sent, a smart reminder sequence fires via delay, and no-show prevention with waitlist management handles follow-up.",
    nodes: [
      trigger("Webhook: Patient appointment request", "webhook_app", {
        source: "Patient Portal / EHR / Phone System",
        event: "appointment.request",
        howItStarts:
          "A patient requests an appointment via the patient portal, calls the office and staff enters the request, or an EHR system initiates a follow-up appointment",
        setup:
          "Configure your patient portal or EHR system to POST appointment requests to your workflow URL. Include patient demographics, insurance info, and appointment preferences.",
        inputFields: [],
      }),

      // 2. Insurance eligibility pre-check
      aiNode(
        "2",
        "AI: Extract patient details + verify insurance",
        280,
        "You are a medical office scheduling assistant. Parse the appointment request and extract: patient_name, patient_email, patient_phone, patient_dob, insurance_provider, insurance_member_id, visit_type (new_patient, follow_up, annual_physical, sick_visit, specialist_referral), preferred_provider, preferred_date, preferred_time, preferred_location, urgency (routine, same_day, urgent), and chief_complaint. Also determine if insurance appears valid based on common payer names. Respond JSON.",
        "{webhook_payload}",
        [
          { field: "patient_name", as: "patient_name", type: "string" },
          { field: "patient_email", as: "patient_email", type: "string" },
          { field: "patient_phone", as: "patient_phone", type: "string" },
          { field: "patient_dob", as: "patient_dob", type: "string" },
          {
            field: "insurance_provider",
            as: "insurance_provider",
            type: "string",
          },
          {
            field: "insurance_member_id",
            as: "insurance_member_id",
            type: "string",
          },
          { field: "visit_type", as: "visit_type", type: "string" },
          {
            field: "preferred_provider",
            as: "preferred_provider",
            type: "string",
          },
          { field: "preferred_date", as: "preferred_date", type: "string" },
          { field: "preferred_time", as: "preferred_time", type: "string" },
          {
            field: "preferred_location",
            as: "preferred_location",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "chief_complaint", as: "chief_complaint", type: "string" },
          { field: "insurance_valid", as: "insurance_valid", type: "string" },
        ],
      ),

      // 3. Coverage issue condition
      n("3", "condition", "Is insurance valid?", 520, {
        conditionData: [
          {
            variable: "insurance_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Provider availability search (True — valid insurance)
      aiNode(
        "4",
        "AI: Search provider availability",
        760,
        "You are a medical scheduling optimizer. Based on the patient preferences, visit type, and urgency, find available appointment slots. Consider: provider schedule, visit duration by type (new patient: 60 min, follow-up: 30 min, physical: 45 min, sick visit: 20 min), and location availability. Provide 3 recommended slots ranked by convenience. Respond JSON: {available_slots, recommended_slot_1, recommended_slot_2, recommended_slot_3, visit_duration_minutes, provider_name, provider_location, appointment_available}",
        "Patient: {patient_name}. Visit type: {visit_type}. Provider: {preferred_provider}. Date: {preferred_date}. Time: {preferred_time}. Location: {preferred_location}. Urgency: {urgency}. Complaint: {chief_complaint}.",
        [
          { field: "recommended_slot_1", as: "slot_1", type: "string" },
          { field: "recommended_slot_2", as: "slot_2", type: "string" },
          { field: "recommended_slot_3", as: "slot_3", type: "string" },
          {
            field: "visit_duration_minutes",
            as: "visit_duration",
            type: "number",
          },
          { field: "provider_name", as: "provider_name", type: "string" },
          {
            field: "provider_location",
            as: "provider_location",
            type: "string",
          },
          {
            field: "appointment_available",
            as: "appointment_available",
            type: "string",
          },
        ],
      ),

      // 5. Appointment booking (runCode)
      n("5", "runCode", "Book appointment + generate confirmation", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Book the appointment and generate confirmation details
const appointmentId = "APT-" + Date.now().toString(36).toUpperCase();
const slot = vars.slot_1 || vars.preferred_date + " " + vars.preferred_time;
const visitDuration = parseInt(vars.visit_duration) || 30;
const visitType = vars.visit_type || "follow_up";

// Pre-visit instructions by type
const instructions = {
  new_patient: "Please arrive 30 minutes early to complete paperwork. Bring: photo ID, insurance card, medication list, medical records from previous provider.",
  follow_up: "Please arrive 10 minutes early. Bring: insurance card, list of current medications, any test results.",
  annual_physical: "Please fast for 12 hours before your appointment if bloodwork is ordered. Bring: insurance card, medication list.",
  sick_visit: "Please arrive 15 minutes early. If you have fever, cough, or flu symptoms, please wear a mask.",
  specialist_referral: "Please bring your referral authorization, insurance card, and any imaging or test results.",
};

return {
  appointment_id: appointmentId,
  booked_slot: slot,
  visit_duration_min: visitDuration,
  pre_visit_instructions: instructions[visitType] || instructions.follow_up,
  portal_link: "https://patient.example.com/appointments/" + appointmentId,
  cancellation_deadline: "24 hours before appointment",
  copay_due: "At time of service"
};`,
          outputVariables: [
            { name: "appointment_id", type: "string" },
            { name: "booked_slot", type: "string" },
            { name: "visit_duration_min", type: "number" },
            { name: "pre_visit_instructions", type: "string" },
            { name: "portal_link", type: "string" },
            { name: "cancellation_deadline", type: "string" },
            { name: "copay_due", type: "string" },
          ],
        },
      }),

      // 6. Confirmation email
      emailNode(
        "6",
        "Send appointment confirmation email",
        1240,
        "{patient_email}",
        "Appointment Confirmed: {visit_type} - {booked_slot}",
        "Dear {patient_name},\\n\\nYour appointment has been confirmed.\\n\\nAppointment ID: {appointment_id}\\nVisit Type: {visit_type}\\nProvider: {provider_name}\\nDate/Time: {booked_slot}\\nDuration: {visit_duration_min} minutes\\nLocation: {provider_location}\\n\\nPre-Visit Instructions:\\n{pre_visit_instructions}\\n\\nInsurance: {insurance_provider} ({insurance_member_id})\\nCopay: {copay_due}\\n\\nManage your appointment: {portal_link}\\nCancellation policy: {cancellation_deadline} notice required\\n\\nIf you need to cancel or reschedule, please call our office or visit the patient portal.",
      ),

      // 7. Smart reminder sequence (delay)
      n("7", "delayAction", "Wait until 48 hours before appointment", 1480, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 48, unit: "hours" },
        },
      }),

      // 8. Reminder email
      emailNode(
        "8",
        "Send appointment reminder + no-show prevention",
        1720,
        "{patient_email}",
        "Reminder: Your appointment is coming up - {appointment_id}",
        "Dear {patient_name},\\n\\nThis is a reminder about your upcoming appointment.\\n\\nProvider: {provider_name}\\nDate/Time: {booked_slot}\\nLocation: {provider_location}\\nVisit Type: {visit_type}\\n\\nRemember to:\\n{pre_visit_instructions}\\n\\nIMPORTANT: If you cannot make this appointment, please cancel at least 24 hours in advance so we can offer the slot to another patient. Repeated no-shows may result in a $25 fee.\\n\\nConfirm your appointment: {portal_link}\\n\\nNeed directions? View map: {provider_location}",
      ),

      // 9. No-show prevention SMS
      smsNode(
        "9",
        "Send same-day SMS reminder",
        1960,
        "{patient_phone}",
        "Reminder: You have an appointment today with {provider_name} at {booked_slot}. Location: {provider_location}. If you need to cancel, call us now. Reply C to confirm, R to reschedule.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // valid insurance -> search availability
      e("3", "6", "output_2"), // invalid insurance -> send email about self-pay options
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 787. PATIENT INTAKE FORM BOT (8 nodes)
  // ===================================================================

  "Patient Intake Form Bot": {
    summary:
      "Conversational patient intake that collects medical history and processes insurance. An intake session triggers existing record pre-population, AI conducts conversational collection of medical history, medication reconciliation runs via runCode to check drug interactions, a drug interaction condition routes flagged medications, insurance card OCR captures payer information, consent signatures are collected, and the completed intake is delivered to the EHR system.",
    nodes: [
      trigger("Webhook: Patient intake session started", "webhook_app", {
        source: "Check-In Kiosk / Patient Portal / Tablet",
        event: "patient.intake.started",
        howItStarts:
          "A patient begins the intake process via a check-in kiosk, patient portal, or staff-initiated tablet session",
        setup:
          "Configure your check-in kiosk, patient portal, or intake tablets to POST session start events to your workflow URL. Include patient identifier for record pre-population.",
        inputFields: [],
      }),

      // 2. Existing record pre-population
      aiNode(
        "2",
        "AI: Extract patient ID + pre-populate records",
        280,
        "You are a medical intake specialist. Parse the intake session payload and extract: patient_id, patient_name, patient_email, patient_phone, patient_dob, existing_allergies, existing_medications, existing_conditions, emergency_contact_name, emergency_contact_phone, primary_care_provider, insurance_on_file, and last_visit_date. If any fields are missing, mark them as needs_update. Respond JSON.",
        "{webhook_payload}",
        [
          { field: "patient_id", as: "patient_id", type: "string" },
          { field: "patient_name", as: "patient_name", type: "string" },
          { field: "patient_email", as: "patient_email", type: "string" },
          { field: "patient_phone", as: "patient_phone", type: "string" },
          { field: "patient_dob", as: "patient_dob", type: "string" },
          {
            field: "existing_allergies",
            as: "existing_allergies",
            type: "string",
          },
          {
            field: "existing_medications",
            as: "existing_medications",
            type: "string",
          },
          {
            field: "existing_conditions",
            as: "existing_conditions",
            type: "string",
          },
          {
            field: "emergency_contact_name",
            as: "emergency_contact",
            type: "string",
          },
          {
            field: "emergency_contact_phone",
            as: "emergency_phone",
            type: "string",
          },
          {
            field: "insurance_on_file",
            as: "insurance_on_file",
            type: "string",
          },
          { field: "last_visit_date", as: "last_visit_date", type: "string" },
        ],
      ),

      // 3. AI conversational collection
      aiNode(
        "3",
        "AI: Conversational medical history collection",
        520,
        "You are a compassionate medical intake assistant. Based on the pre-populated patient record, generate a comprehensive intake interview summary as if you conducted a conversational intake. Update any fields marked needs_update. Collect: current_medications (with dosages), allergies (medication, food, environmental), surgical_history, family_history, social_history (smoking, alcohol, exercise), current_symptoms, pain_level (0-10), reason_for_visit, and review_of_systems. Format medication list for reconciliation. Respond JSON: {current_medications_list, allergy_list, surgical_history, family_history, social_history, current_symptoms, pain_level, reason_for_visit, review_of_systems, fields_updated}",
        "Patient: {patient_name} (DOB: {patient_dob}, ID: {patient_id}). Existing allergies: {existing_allergies}. Existing meds: {existing_medications}. Existing conditions: {existing_conditions}. Last visit: {last_visit_date}.",
        [
          {
            field: "current_medications_list",
            as: "current_medications",
            type: "string",
          },
          { field: "allergy_list", as: "allergy_list", type: "string" },
          { field: "surgical_history", as: "surgical_history", type: "string" },
          { field: "family_history", as: "family_history", type: "string" },
          { field: "social_history", as: "social_history", type: "string" },
          { field: "current_symptoms", as: "current_symptoms", type: "string" },
          { field: "pain_level", as: "pain_level", type: "number" },
          { field: "reason_for_visit", as: "reason_for_visit", type: "string" },
          {
            field: "review_of_systems",
            as: "review_of_systems",
            type: "string",
          },
          { field: "fields_updated", as: "fields_updated", type: "string" },
        ],
      ),

      // 4. Medication reconciliation (runCode)
      n("4", "runCode", "Reconcile medications + check interactions", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Medication reconciliation and drug interaction check
const currentMeds = (vars.current_medications || "").toLowerCase();
const allergies = (vars.allergy_list || "").toLowerCase();

// Simulated interaction database
const knownInteractions = [
  { drug1: "warfarin", drug2: "aspirin", severity: "major", effect: "Increased bleeding risk" },
  { drug1: "lisinopril", drug2: "potassium", severity: "major", effect: "Hyperkalemia risk" },
  { drug1: "metformin", drug2: "contrast dye", severity: "major", effect: "Lactic acidosis risk" },
  { drug1: "ssri", drug2: "tramadol", severity: "major", effect: "Serotonin syndrome risk" },
  { drug1: "statin", drug2: "grapefruit", severity: "moderate", effect: "Increased statin levels" },
  { drug1: "methotrexate", drug2: "nsaid", severity: "major", effect: "Methotrexate toxicity" },
];

const interactions = [];
knownInteractions.forEach(i => {
  if (currentMeds.includes(i.drug1) && currentMeds.includes(i.drug2)) {
    interactions.push(i.severity.toUpperCase() + ": " + i.drug1 + " + " + i.drug2 + " => " + i.effect);
  }
});

// Check for allergies in current medications
const allergyFlags = [];
const commonAllergens = ["penicillin", "sulfa", "aspirin", "ibuprofen", "codeine", "latex"];
commonAllergens.forEach(a => {
  if (allergies.includes(a) && currentMeds.includes(a)) {
    allergyFlags.push("ALLERGY ALERT: Patient allergic to " + a + " but it appears in current medications");
  }
});

const hasInteraction = (interactions.length > 0 || allergyFlags.length > 0) ? "yes" : "no";
const allFlags = [...interactions, ...allergyFlags];

return {
  has_drug_interaction: hasInteraction,
  interaction_details: allFlags.join("; ") || "No interactions detected",
  interaction_count: allFlags.length,
  interaction_severity: interactions.some(i => i.includes("MAJOR")) ? "major" : interactions.length > 0 ? "moderate" : "none",
  medication_count: currentMeds.split(",").filter(Boolean).length,
  reconciliation_status: hasInteraction === "yes" ? "REVIEW_REQUIRED" : "COMPLETED",
  reconciliation_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "has_drug_interaction", type: "string" },
            { name: "interaction_details", type: "string" },
            { name: "interaction_count", type: "number" },
            { name: "interaction_severity", type: "string" },
            { name: "medication_count", type: "number" },
            { name: "reconciliation_status", type: "string" },
            { name: "reconciliation_date", type: "string" },
          ],
        },
      }),

      // 5. Drug interaction condition
      n("5", "condition", "Any drug interactions detected?", 1000, {
        conditionData: [
          {
            variable: "has_drug_interaction",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Insurance card OCR capture (handles both paths)
      aiNode(
        "6",
        "AI: Process insurance information",
        1240,
        "You are a medical insurance data processor. Based on the patient insurance information on file, verify and structure the insurance details for the EHR. Extract: insurance_company, plan_name, member_id, group_number, subscriber_name, subscriber_dob, relationship_to_subscriber, copay_amount, authorization_required. If insurance_on_file is empty or outdated, flag for front desk to collect insurance card. Respond JSON.",
        "Patient: {patient_name} (DOB: {patient_dob}). Insurance on file: {insurance_on_file}. Patient ID: {patient_id}.",
        [
          {
            field: "insurance_company",
            as: "insurance_company",
            type: "string",
          },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "member_id", as: "member_id", type: "string" },
          { field: "group_number", as: "group_number", type: "string" },
          { field: "copay_amount", as: "copay_amount", type: "string" },
          {
            field: "authorization_required",
            as: "auth_required",
            type: "string",
          },
        ],
      ),

      // 7. Consent signatures collection (sendMessage)
      n("7", "sendMessage", "Collect electronic consent signatures", 1480, {
        sendMessageDetails: {
          message:
            "Intake Summary for {patient_name} ({patient_id})\\n\\nPlease review and provide electronic consent for the following:\\n\\n1. HIPAA Privacy Notice - I acknowledge receipt of the Notice of Privacy Practices\\n2. Consent to Treat - I consent to examination and treatment\\n3. Financial Responsibility - I accept financial responsibility for services\\n4. Release of Information - I authorize release of medical records as needed for treatment and billing\\n\\nMedication Reconciliation Status: {reconciliation_status}\\nInteractions Found: {interaction_details}\\n\\nAllergies: {allergy_list}\\nCurrent Medications: {current_medications}\\nReason for Visit: {reason_for_visit}\\nPain Level: {pain_level}/10\\n\\nPlease sign electronically to confirm all information is accurate.",
        },
      }),

      // 8. Completed intake delivery to EHR
      emailNode(
        "8",
        "Deliver completed intake to care team",
        1720,
        "{care_team_email}",
        "Intake Complete: {patient_name} ({patient_id}) - {reason_for_visit}",
        "Patient Intake Summary\\n\\nPatient: {patient_name}\\nDOB: {patient_dob}\\nID: {patient_id}\\nDate: {reconciliation_date}\\n\\nReason for Visit: {reason_for_visit}\\nCurrent Symptoms: {current_symptoms}\\nPain Level: {pain_level}/10\\n\\nAllergies: {allergy_list}\\nCurrent Medications: {current_medications}\\nMedication Count: {medication_count}\\nReconciliation Status: {reconciliation_status}\\nDrug Interactions: {interaction_details}\\nInteraction Severity: {interaction_severity}\\n\\nSurgical History: {surgical_history}\\nFamily History: {family_history}\\nSocial History: {social_history}\\nReview of Systems: {review_of_systems}\\n\\nInsurance: {insurance_company} ({plan_name})\\nMember ID: {member_id}\\nGroup: {group_number}\\nCopay: {copay_amount}\\nAuth Required: {auth_required}\\n\\nEmergency Contact: {emergency_contact} ({emergency_phone})\\n\\nConsent signatures collected electronically. Fields updated: {fields_updated}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // interaction detected -> insurance processing (pharmacist will review)
      e("5", "6", "output_2"), // no interaction -> insurance processing
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
